import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shouldIgnoreAppNavigationShortcut } from '../utils/appNavigationShortcutGuard';
import { isWeekdayTimeKey, resolveClassScheduleRowsForWeekday } from '../utils/classScheduleWeekdayTimes';
import { WeeklyLearningGuideEmptyFrame } from '../components/common/ActualEmptyDataFrames';
import ModalDocumentActionButtons from '../components/common/ModalDocumentActionButtons';
import WeeklyLearningGuideNeisLegend from '../components/weeklyLearningGuide/WeeklyLearningGuideNeisLegend';
import WeeklyGuideMaterialMarquee from '../components/weeklyLearningGuide/WeeklyGuideMaterialMarquee';
import WeeklyLearningGuideNeisCellContent from '../components/weeklyLearningGuide/WeeklyLearningGuideNeisCellContent';
import DashboardWeeklyLessonActionMenu from '../components/dashboard/DashboardWeeklyLessonActionMenu';
import {
  parseDashboardEvalStatusCode,
  type DashboardEvalStatusCode,
} from '../components/dashboard/DashboardLessonEvalStatusBadge';
import DashboardLessonSlotRecordModals from '../components/dashboard/DashboardLessonSlotRecordModals';
import { useDashboardLessonSlotRecords } from '../hooks/useDashboardLessonSlotRecords';
import {
  buildDashboardWeeklyDaySlots,
  type DashboardAnnualDaySlot,
  type DashboardLessonSectionBundle,
} from '../utils/dashboardAnnualDaySlots';
import { openDashboardAnnualSlotPresentation } from '../utils/openDashboardAnnualSlotPresentation';
import {
  annualSlotHasTimetableProgramAssignment,
  buildScopedRowsAnnualIndex,
  computeScopedOccurrenceForAnnualSlot,
  resolveAnnualSlotProgressSource,
  type ScopedRowsAnnualIndex,
} from '../utils/annualProgressSlotSource';
import { loadTrackProgramModeMap } from '../utils/trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from '../utils/trackProgramSyncRepStorage';
import { buildDashboardSlotProgramPath } from '../utils/dashboardSlotProgramPath';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useWeeklyLearningGuidePageQueries } from '../hooks/useWeeklyLearningGuidePageQueries';
import { schoolKeys } from '../query/keys';
import { syncSchoolCachesWithQuery } from '../query/syncSchoolCachesWithQuery';
import {
  upsertWeeklyLearningGuideNotes,
} from '../services/weeklyLearningGuideService';
import {
  upsertWeeklyLearningGuideTrackColors,
} from '../services/weeklyLearningGuideTrackColorsService';
import {
  BaseTimetableGrid,
  getBaseTimetableGridForTerm,
  loadClassScheduleSettingsCache,
  loadGradeTrackSectionsCache,
} from '../services/sectionLayoutService';
import {
  AcademicTerm,
  AnnualTimetableCell,
  AnnualTimetableSlotKind,
  AnnualWeekdayKey,
  CalendarDay,
  DayKind,
  Grade,
  ProgramFixedContentRow,
  ProgramProgressCell,
  Track,
  WeeklyLearningGuideNote,
} from '../types/domain';
import {
  buildDefaultWeeklyGuideTrackColorMap,
  intensifyLightWeeklyGuideColorHex,
  mergeWeeklyGuideTrackColorMap,
  normalizeWeeklyGuideColorHex,
  readWeeklyGuideColorMode,
  WEEKLY_GUIDE_CELL_MODE_FALLBACK_HEX,
  WEEKLY_GUIDE_COLOR_MODE_KEY,
  type WeeklyGuideColorMode,
} from '../constants/weeklyLearningGuide';
import {
  openWeeklyLearningGuideA4PreviewFailureMessage,
  openWeeklyLearningGuideA4PreviewInNewTab,
  openWeeklyLearningGuideA4PreviewTabPlaceholder,
} from '../utils/weeklyLearningGuideA4PreviewStorage';
import { resolveNeisShortEdgeColor } from '../utils/neisShortEdgeColor';
import { subjectTrackTintRgba } from '../utils/subjectTrackTint';
import {
  buildNeisShortIncreaseEdgeStyle,
  type NeisVsBaseKind,
} from '../utils/weeklyLearningGuideNeisCellStyle';
import {
  collectOtherTrackLabelsForGrade,
  isWeeklyGuideHiddenOtherTrack,
} from '../utils/weeklyLearningGuideOtherTracks';
import {
  saveWeeklyLearningGuideAsExcel,
  type WeeklyLearningGuideExportPayload,
  type WeeklyLearningGuideExportWeekBlock,
} from '../utils/weeklyLearningGuideExcelExport';
import {
  weeklyGuideSurfaceClassNames,
  weeklySlotSurfaceFromEffective,
  type WeeklyGuideSlotSurface,
} from '../utils/weeklyLearningGuideSurface';
import {
  ANNUAL_WEEKDAY_KEYS,
  buildAnnualCellKey,
  buildAnnualClassEntries,
  baseGridClassPeriodIndexForSectionSlot,
  dateIsoForMondayWeekday,
  type AnnualEffectiveCell,
  deriveAnnualEffectiveCell,
  getBaseGridCell,
  getSeedSlotKind,
  isAnnualShortDayTailMemo,
  isDedicatedColor,
  isFixedProgramColor,
  normalizeColorHexForCompare,
} from '../utils/annualTimetable';
import {
  AnnualProjectProgram,
  matchesAnnualProjectProgramCell,
} from '../utils/annualProjectProgramsStorage';
import { timetableTextForOffDayRow, type OffDayByDate } from '../utils/annualProgressDataCache';
import { mergeWeeklyLearningGuideNotesCache } from '../utils/weeklyLearningGuideNotesCache';
import { addDays, mondayOf, parseISODate, toISODate } from '../utils/date';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import {
  formatLearningTextForDisplay,
  getUnitTextAndNoteForDisplay,
  parseProgramProgressMemoJsonForDisplay,
} from '../utils/programCellDisplayText';
import { buildTrackNameByTimetableTextByGrade } from '../utils/programProgressRows';
import { getOrderedTrackNamesForGrade } from '../utils/trackOrderForGrade';
import NeisExtensionButton from '../components/weeklyLearningGuide/NeisExtensionButton';

function weeklyGuideColorPickerValue(raw: string): string {
  return normalizeWeeklyGuideColorHex(raw) || '#888888';
}

type TermNumber = 1 | 2;

type TermMap<T> = { 1: T; 2: T };

interface WeeklyLearningGuidePageProps {
  workspaceId: string;
  currentGradeId: string;
  grades: Grade[];
  /** 대시보드 「시간표」 모드에 끼워 넣을 때 */
  embedded?: WeeklyLearningGuideEmbeddedOptions;
}

export interface WeeklyLearningGuideEmbeddedOptions {
  /** true: 교시 행 대신 요일·날짜 열 아래 TODO 목록 */
  todoMode: boolean;
  onTodoModeChange: (next: boolean) => void;
  onViewedWeekChange?: (mondayIso: string) => void;
  renderDayTodos?: (dateIso: string) => React.ReactNode;
  /** 호스트 페이지 모달이 열려 있으면 화살표 키 무시 */
  keyboardDisabled?: boolean;
  /** 호스트 상단 바(주 이동)용 상태 */
  onNavStateChange?: (state: WeeklyLearningGuideEmbeddedNavState) => void;
  /** 호스트 상단 바 버튼 → 주 이동 */
  navControlRef?: React.MutableRefObject<WeeklyLearningGuideEmbeddedNavControl | null>;
  /** 호스트(대시보드 위클리)와 같은 학년도 — 같은 RQ/LS 캐시 키를 쓰게 함 */
  academicYear?: number;
  /** 호스트가 이미 불러온 오늘 학기. 첫 로드는 이 학기만 조회 */
  initialTermNumber?: TermNumber | null;
  /** 학사일정 섹션이 로컬 일정을 쓰는 클래스면 그 목록, DB 일정이면 null */
  linkedCalendarDays?: CalendarDay[] | null;
  /** 호스트(대시보드 데일리·위클리)의 수업 번들 — 수업 블럭 내용을 같은 데이터로 표시 */
  lessonBundle?: DashboardLessonSectionBundle | null;
}

export interface WeeklyLearningGuideEmbeddedNavState {
  termNumber: number | null;
  weekIndex: number | null;
  canPrev: boolean;
  canNext: boolean;
}

export interface WeeklyLearningGuideEmbeddedNavControl {
  prev: () => void;
  next: () => void;
  today: () => void;
}

interface WeekInfo {
  weekIndex: number;
  mondayIso: string;
  dateRangeLabel: string;
  datesByWeekday: string[];
}

interface WeeklyDayColumn {
  weekdayKey: AnnualWeekdayKey;
  weekdayLabel: string;
  dateIso: string;
  inTerm: boolean;
}

interface PeriodSlot {
  slotIndex: number;
  label: string;
  startTime: string;
  endTime: string;
}

const PERIOD_TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function normalizePeriodTime(raw: string | undefined): string {
  const m = PERIOD_TIME_RE.exec((raw ?? '').trim());
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

function periodEndTime(startTime: string, endRaw: string | undefined, durationRaw: string | undefined): string {
  const end = normalizePeriodTime(endRaw);
  if (end || !startTime) return end;
  const minutes = Number((durationRaw ?? '').trim());
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  const [h, m] = startTime.split(':').map(Number);
  const total = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** 클릭한 칸이 속한 수업 카드(같은 교시·요일의 세로 칸 묶음) 전체 영역 */
function weeklyGuideCardRect(anchorEl: HTMLElement, weekdayKey: string): DOMRect {
  const cells = anchorEl.closest('tbody')?.querySelectorAll<HTMLElement>(`td[data-wlg-day="${weekdayKey}"]`);
  if (!cells || cells.length === 0) return anchorEl.getBoundingClientRect();
  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  cells.forEach((cell) => {
    const r = cell.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  });
  if (!Number.isFinite(top)) return anchorEl.getBoundingClientRect();
  return new DOMRect(left, top, right - left, bottom - top);
}

const WEEKLY_GUIDE_EVAL_LABELS: Record<DashboardEvalStatusCode, string> = {
  '1': '평가계획',
  '2': '평가중',
  '3': '평가완료',
};

const WEEKLY_GUIDE_EVAL_TONES: Record<DashboardEvalStatusCode, string> = {
  '1': 'is-plan',
  '2': 'is-progress',
  '3': 'is-done',
};

function PeriodTitleContent({ period }: { period: PeriodSlot }) {
  return (
    <>
      {period.label}
      {period.startTime || period.endTime ? (
        <span className="cp-weekly-learning-guide-period-time">
          <span>{period.startTime}</span>
          <span>{period.endTime}</span>
        </span>
      ) : null}
    </>
  );
}

interface WeeklyCellDisplay {
  subject: string;
  /** 화면 블럭 헤더: 편제명(없으면 시간표명) */
  subjectTitle?: string;
  /** 화면 블럭 헤더: 편제명과 다를 때의 시간표명 */
  subjectSubtitle?: string;
  /** 편제(등록 순)별 시간표 행 배경 — `trackOrderForGrade` 순서와 14색 팔레트 매칭 */
  subjectTrackBgHex?: string;
  unit: string;
  learning: string;
  page: string;
  material: string;
  /** NEIS 반별시간표 모드: 편제명만(큰 글씨) */
  neisTrackName?: string;
  /** NEIS 모드: 편제명 아래 (전담) 표시 */
  neisDedicated?: boolean;
  /** NEIS 모드: 편제 미등록 시간표 입력 텍스트 */
  neisUnregisteredTimetableText?: string;
  /** NEIS 모드: 기초시간표 대비 구분 */
  neisVsBase?: NeisVsBaseKind;
  /** 연간 시간표와 동일 계열의 비수업·휴일·활동 배경 */
  slotSurface: WeeklyGuideSlotSurface;
  /**
   * NEIS: 편제 시드(요일·기준 교시) 대비 연간에 수업↔비수업만 바뀐 교시(증배·단축일 반영)에만 메인 초록 안쪽 테두리.
   */
  neisShortIncreaseHighlight: boolean;
}

const NEIS_CLASS_TIMETABLE_MODE_KEY = 'cp.weeklyLearningGuide.neisClassTimetableMode';

function classifyNeisCellVsBase(params: {
  annualTimetableText: string;
  annualTemplate: string;
  annualColorHex: string;
  baseTimetableText: string;
  baseTemplate: string;
  baseColorHex: string;
  dedicatedColors: string[];
}): NeisVsBaseKind {
  const {
    annualTimetableText,
    annualTemplate,
    annualColorHex,
    baseTimetableText,
    baseTemplate,
    baseColorHex,
    dedicatedColors,
  } = params;
  const annT = annualTimetableText.trim();
  const baseT = baseTimetableText.trim();
  const annP = (annualTemplate || '').trim();
  const baseP = (baseTemplate || '').trim();
  const sameTimetableText = annT === baseT;
  const sameTrackTemplate = !!annP && !!baseP && annP === baseP;
  const sameTrack = sameTimetableText || sameTrackTemplate;

  if (!sameTrack) {
    return 'diff-track';
  }
  const annDed = isDedicatedColor(annualColorHex, dedicatedColors);
  const baseDed = isDedicatedColor(baseColorHex, dedicatedColors);
  const colorDiff =
    normalizeColorHexForCompare(annualColorHex) !== normalizeColorHexForCompare(baseColorHex);
  if (annDed !== baseDed || (annDed && baseDed && colorDiff)) {
    return 'dedicated-mismatch';
  }
  return 'match-base';
}

interface FixedEventSummary {
  dateIso: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  eventName: string;
}

type ProgramProgressLikeRow = Pick<
  ProgramProgressCell,
  | 'unit_text'
  | 'learning_text'
  | 'page_text'
  | 'assistant_page_text'
  | 'material_text'
  | 'record_class_text'
>;

function normalizeFixedEventName(raw: string): string {
  return raw.trim().replace(/^◆+/, '').replace(/-+$/, '').trim();
}

function eventNameFromFixedContentRow(row: ProgramFixedContentRow): string {
  const byEvent = normalizeFixedEventName(row.event_name ?? '');
  if (byEvent) return byEvent;
  const byUnit = normalizeFixedEventName(row.unit_text ?? '');
  if (byUnit) return byUnit;
  const learning = (row.learning_text ?? '').trim();
  const match = learning.match(/^◆\s*([^-]+?)\s*-/);
  return match?.[1] ? normalizeFixedEventName(match[1]) : '';
}

function mapProgressLikeToDisplayFields(src: ProgramProgressLikeRow | null): {
  unit: string;
  learning: string;
  page: string;
  assistantPage: string;
  material: string;
} {
  if (!src) {
    return {
      unit: '',
      learning: '',
      page: '',
      assistantPage: '',
      material: '',
    };
  }
  const unitParsed = getUnitTextAndNoteForDisplay(src.unit_text ?? '');
  const learningParsed = parseProgramProgressMemoJsonForDisplay(src.learning_text ?? '');
  return {
    unit: formatLearningTextForDisplay(unitParsed.text),
    learning: formatLearningTextForDisplay(learningParsed.text),
    page: (src.page_text ?? '').trim(),
    assistantPage: (src.assistant_page_text ?? '').trim(),
    material: (src.material_text ?? '').trim(),
  };
}

function formatSubjectLabel(templateLabel: string, timetableText: string): string {
  const template = templateLabel.trim();
  const timetable = timetableText.trim();
  if (template && timetable && template !== timetable) return `${template} (${timetable})`;
  return template || timetable;
}

function buildWeeksFromRange(startsOn: string, endsOn: string): WeekInfo[] {
  if (!startsOn || !endsOn) return [];
  const formatMMDD = (date: Date) =>
    `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  const endDate = parseISODate(endsOn);
  const endMonday = mondayOf(endsOn);
  const endMondayIso = toISODate(endMonday);
  let cur = mondayOf(startsOn);
  const list: WeekInfo[] = [];
  let weekIndex = 0;
  while (cur.getTime() <= endDate.getTime()) {
    const mondayIso = toISODate(cur);
    const mon = parseISODate(mondayIso);
    const sat = addDays(cur, 5);
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(cur, d)));
    }
    list.push({
      weekIndex: ++weekIndex,
      mondayIso,
      dateRangeLabel: `${formatMMDD(mon)}-${formatMMDD(sat)}`,
      datesByWeekday,
    });
    cur = addDays(cur, 7);
  }
  const hasEndWeek = list.some((week) => week.mondayIso === endMondayIso);
  if (!hasEndWeek) {
    const mon = parseISODate(endMondayIso);
    const sat = addDays(endMonday, 5);
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(endMonday, d)));
    }
    list.push({
      weekIndex: ++weekIndex,
      mondayIso: endMondayIso,
      dateRangeLabel: `${formatMMDD(mon)}-${formatMMDD(sat)}`,
      datesByWeekday,
    });
  }
  return list;
}

function weekLastDateIso(week: WeekInfo): string {
  return week.datesByWeekday[week.datesByWeekday.length - 1] ?? week.mondayIso;
}

/** 오늘이 속한 주 → 없으면 목록에서 오늘(월 기준) 이후 가장 가까운 주 → 모두 과거면 마지막 주 */
function pickDefaultWeekMondayIso(weeks: WeekInfo[], todayIso: string): string {
  if (!weeks.length) return '';
  const lastEnd = weekLastDateIso(weeks[weeks.length - 1]);
  if (todayIso > lastEnd) return weeks[weeks.length - 1].mondayIso;
  const containing = weeks.find((w) => {
    const end = weekLastDateIso(w);
    return todayIso >= w.mondayIso && todayIso <= end;
  });
  if (containing) return containing.mondayIso;
  const mondayOfTodayWeek = toISODate(mondayOf(todayIso));
  const future = weeks.find((w) => w.mondayIso >= mondayOfTodayWeek);
  if (future) return future.mondayIso;
  return weeks[weeks.length - 1].mondayIso;
}

function preferredTermForToday(terms: AcademicTerm[], todayIso: string, availableTerms: TermNumber[]): TermNumber | null {
  for (const n of [1, 2] as TermNumber[]) {
    if (!availableTerms.includes(n)) continue;
    const t = terms.find((x) => x.term_number === n);
    if (!t) continue;
    if (todayIso >= t.starts_on && todayIso <= t.ends_on) return n;
  }
  return null;
}

function formatWeekOptionLabel(week: WeekInfo): string {
  return `${week.weekIndex}주 (${week.dateRangeLabel})`;
}

function formatWeeklyGuideContainerTitle(term: number, week: WeekInfo): string {
  const [start, end] = week.dateRangeLabel.split('-');
  return `${term}학기 ${week.weekIndex}주 주간학습 안내 (${start} -${end})`;
}

function formatWeeklyGuideContainerTitleParts(term: number, week: WeekInfo): {
  main: string;
  dates: string;
} {
  const [start, end] = week.dateRangeLabel.split('-');
  return {
    main: `${term}학기 ${week.weekIndex}주 주간학습 안내`,
    dates: `(${start} -${end})`,
  };
}

/**
 * 화면 블럭형 표에서 교시 카드 사이 세로 간격. tr 의 hidden 은 화면 CSS 에서만 풀리므로
 * PDF·인쇄 캡처(tbody 병합 후)에는 나타나지 않는다.
 */
function renderWeeklyGuideBlockGap(dayColumnCount: number): React.ReactElement {
  return (
    <tbody className="cp-weekly-learning-guide-block-gap" aria-hidden="true">
      <tr hidden>
        <td colSpan={dayColumnCount + 1} />
      </tr>
    </tbody>
  );
}

function formatDateHeader(iso: string): string {
  if (!iso) return '';
  const date = parseISODate(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

function formatActivityDate(iso: string, weekdayLabel: string): string {
  const date = parseISODate(iso);
  if (Number.isNaN(date.getTime())) return `${iso} (${weekdayLabel})`;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}.${day} (${weekdayLabel})`;
}

function formatPageText(pageText: string, assistantPageText: string): string {
  const main = (pageText ?? '').trim();
  const assist = (assistantPageText ?? '').trim();
  if (main && assist) return `${main} / 보조 ${assist}`;
  if (main) return main;
  if (assist) return `보조 ${assist}`;
  return '';
}


function weeklySubjectTrackTintStyle(
  display: WeeklyCellDisplay,
  row: 'learning' | 'page' | 'material'
): React.CSSProperties | undefined {
  if (display.slotSurface !== 'class' || !display.subjectTrackBgHex) return undefined;
  return { backgroundColor: subjectTrackTintRgba(display.subjectTrackBgHex) };
}

function buildProgressMap(rows: ProgramProgressCell[]): Record<string, ProgramProgressCell> {
  return rows.reduce<Record<string, ProgramProgressCell>>((acc, row) => {
    acc[buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index)] = row;
    return acc;
  }, {});
}

function buildAnnualMap(rows: AnnualTimetableCell[]): Record<string, AnnualTimetableCell> {
  return rows.reduce<Record<string, AnnualTimetableCell>>((acc, row) => {
    acc[buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index)] = row;
    return acc;
  }, {});
}

function buildNoteMap(rows: WeeklyLearningGuideNote[]): Record<string, WeeklyLearningGuideNote> {
  return rows.reduce<Record<string, WeeklyLearningGuideNote>>((acc, row) => {
    acc[row.monday_iso] = row;
    return acc;
  }, {});
}

const WeeklyLearningGuidePage: React.FC<WeeklyLearningGuidePageProps> = ({
  workspaceId,
  currentGradeId,
  grades,
  embedded,
}) => {
  const queryClient = useQueryClient();
  const [locationSearchParams, setSearchParams] = useSearchParams();
  const emptySearchParams = React.useMemo(() => new URLSearchParams(), []);
  const searchParams = embedded ? emptySearchParams : locationSearchParams;
  const embeddedTodoMode = Boolean(embedded?.todoMode);
  const {
    programFocusGradeId,
    setProgramFocusGradeId,
    programSidebarAcademicYear,
    setProgramSidebarAcademicYear,
  } = useProgramSidebar();

  const gradeIdFromUrl = searchParams.get('gradeId');
  const termFromUrlRaw = searchParams.get('term')?.trim() ?? '';
  const termFromUrl: TermNumber | null =
    termFromUrlRaw === '1' || termFromUrlRaw === '2' ? (Number(termFromUrlRaw) as TermNumber) : null;
  const neisAutoExportRaw = (searchParams.get('neisAutoExport') ?? '').trim().toLowerCase();
  const neisAutoExportFormat: 'xls' | 'pdf' | null =
    neisAutoExportRaw === 'xls' || neisAutoExportRaw === 'xlsx'
      ? 'xls'
      : neisAutoExportRaw === 'pdf'
        ? 'pdf'
        : null;
  /** 대시보드 임베드는 위클리와 같은 학급·학년도·학기로 조회해 캐시를 공유한다 */
  const effectiveGradeId = embedded
    ? currentGradeId || ''
    : gradeIdFromUrl || programFocusGradeId || currentGradeId || '';
  const currentYear = new Date().getFullYear();
  const [pageAcademicYear] = React.useState(programSidebarAcademicYear ?? currentYear);
  const academicYear = embedded?.academicYear ?? pageAcademicYear;
  const embeddedInitialTerm = embedded?.initialTermNumber ?? null;
  /** 첫 주 선택 전에 조회할 학기(호스트 학기 → URL 학기). 없으면 학기 정보로 오늘 학기를 고른다 */
  const presetInitialTerm: TermNumber | null = embeddedInitialTerm ?? (embedded ? null : termFromUrl);
  const [loadTermNumbers, setLoadTermNumbers] = React.useState<TermNumber[]>(() =>
    presetInitialTerm ? [presetInitialTerm] : []
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [inlineNoticeText, setInlineNoticeText] = React.useState('');
  const [inlineNoticeSaving, setInlineNoticeSaving] = React.useState(false);
  const [inlineNoticeError, setInlineNoticeError] = React.useState<string | null>(null);
  const noticeTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const weeklyGuideExportRootRef = React.useRef<HTMLDivElement | null>(null);
  const [weeklyGuideExportBusy, setWeeklyGuideExportBusy] = React.useState(false);
  const headerTodayIso = toISODate(new Date());
  const [modalOpen, setModalOpen] = React.useState(false);
  const [trackColorModalOpen, setTrackColorModalOpen] = React.useState(false);
  const [draftTrackColorMap, setDraftTrackColorMap] = React.useState<Record<string, string>>({});
  const [draftColorMode, setDraftColorMode] = React.useState<WeeklyGuideColorMode>('track');
  const [trackColorModalSaving, setTrackColorModalSaving] = React.useState(false);
  const [trackColorModalError, setTrackColorModalError] = React.useState<string | null>(null);
  const [trackColorResetConfirmOpen, setTrackColorResetConfirmOpen] = React.useState(false);
  const [termSelection, setTermSelection] = React.useState<TermNumber>(
    () => termFromUrl ?? embedded?.initialTermNumber ?? 1
  );
  const [selectedWeekByTerm, setSelectedWeekByTerm] = React.useState<TermMap<string>>({ 1: '', 2: '' });
  const [viewedTerm, setViewedTerm] = React.useState<TermNumber | null>(null);
  const [viewedMondayIso, setViewedMondayIso] = React.useState('');
  /** 워크스페이스·학년도·학년마다 한 번만: 오늘 기준 주·학기 자동 선택 */
  const weeklyGuideOpenPickRef = React.useRef('');
  const neisAutoExportDoneRef = React.useRef('');
  const [draftNotes, setDraftNotes] = React.useState<Record<string, string>>({});
  const [neisClassTimetableModePref, setNeisClassTimetableMode] = React.useState(
    () => neisAutoExportFormat != null
  );
  /** 대시보드 임베드는 항상 일반(카드형) 시간표 */
  const neisClassTimetableMode = embedded ? false : neisClassTimetableModePref;
  const [lessonMenu, setLessonMenu] = React.useState<{
    slot: DashboardAnnualDaySlot;
    rect: DOMRect;
  } | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (neisAutoExportFormat != null) {
      setNeisClassTimetableMode(true);
      try {
        window.localStorage.setItem(NEIS_CLASS_TIMETABLE_MODE_KEY, '1');
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const v = window.localStorage.getItem(NEIS_CLASS_TIMETABLE_MODE_KEY);
      if (v === '1' || v === 'true') setNeisClassTimetableMode(true);
    } catch {
      /* ignore */
    }
  }, [neisAutoExportFormat]);

  React.useEffect(() => {
    if (termFromUrl == null) return;
    setTermSelection(termFromUrl);
  }, [termFromUrl]);

  const toggleNeisClassTimetableMode = React.useCallback(() => {
    setNeisClassTimetableMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(NEIS_CLASS_TIMETABLE_MODE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (embedded) return;
    setProgramSidebarAcademicYear(academicYear);
  }, [academicYear, embedded, setProgramSidebarAcademicYear]);

  React.useEffect(() => {
    if (embedded) return;
    if (effectiveGradeId) setProgramFocusGradeId(effectiveGradeId);
  }, [effectiveGradeId, embedded, setProgramFocusGradeId]);

  const gradeRow = React.useMemo(
    () => (effectiveGradeId ? grades.find((grade) => grade.id === effectiveGradeId) ?? null : null),
    [effectiveGradeId, grades]
  );
  const classParsed = React.useMemo(() => (gradeRow ? parseGradeDisplayName(gradeRow.name) : null), [gradeRow]);

  /** LS 캐시로 먼저 그리고, 학교 데이터 revision 비교는 백그라운드(바뀐 파트만 RQ에서 제거·재조회) */
  React.useEffect(() => {
    if (!workspaceId || embedded) return;
    void syncSchoolCachesWithQuery(workspaceId).catch(() => {
      /* sync 실패 시에도 LS/RQ fetch가 자체 처리 */
    });
  }, [embedded, workspaceId]);

  React.useEffect(() => {
    if (modalOpen) {
      setLoadTermNumbers([1, 2]);
    }
  }, [modalOpen]);

  const pageQueries = useWeeklyLearningGuidePageQueries({
    workspaceId,
    gradeId: effectiveGradeId,
    grades,
    academicYear,
    loadTermNumbers,
    schoolScopedCacheSyncReady: true,
    enabled: !!effectiveGradeId,
    linkedCalendarDays: embedded?.linkedCalendarDays ?? null,
  });

  const {
    terms,
    schedule,
    classScheduleSettings,
    tracks,
    regularColors,
    dedicatedColors,
    baseExcludeColors,
    baseTimetableByGrade,
    annualProjectPrograms,
    annualRowsByTerm,
    progressRowsByTerm,
    calendarDaysByTerm,
    savedNotesByTerm,
    scopedRowsByTerm,
    fixedContentRowsByTerm,
    savedTrackColorMap,
    nonClassHolidaySetByTerm,
    calendarDayKindByTerm,
    offDayByDateByTerm,
    loading,
    notesLoading,
    error: queryError,
  } = pageQueries;

  React.useEffect(() => {
    setError(queryError);
  }, [queryError]);

  /** 학기 단위 로드: 첫 주 선택 전에는 오늘 학기 하나만, 이후엔 보고 있는·선택한 학기를 추가 */
  React.useEffect(() => {
    setLoadTermNumbers((prev) => {
      const next = new Set(prev);
      if (!viewedTerm) {
        const initialTerm =
          presetInitialTerm ?? preferredTermForToday(terms, toISODate(new Date()), [1, 2]);
        if (initialTerm) next.add(initialTerm);
      } else {
        next.add(termSelection);
        next.add(viewedTerm);
      }
      const out = ([1, 2] as TermNumber[]).filter((term) => next.has(term));
      return out.length === prev.length && out.every((term, i) => term === prev[i]) ? prev : out;
    });
  }, [presetInitialTerm, termSelection, terms, viewedTerm]);

  const updateWeeklyGuideNotesQueryCache = React.useCallback(
    (termNumber: TermNumber, savedRows: WeeklyLearningGuideNote[]) => {
      if (!workspaceId || !effectiveGradeId || savedRows.length === 0) return;
      const queryKey = schoolKeys.weeklyLearningGuideNotes(
        workspaceId,
        academicYear,
        termNumber,
        effectiveGradeId
      );
      queryClient.setQueryData<WeeklyLearningGuideNote[]>(queryKey, (prev: WeeklyLearningGuideNote[] | undefined) =>
        mergeWeeklyLearningGuideNotesCache(prev ?? [], savedRows)
      );
    },
    [academicYear, effectiveGradeId, queryClient, workspaceId]
  );

  const updateWeeklyGuideTrackColorsQueryCache = React.useCallback(
    (map: Record<string, string>) => {
      if (!workspaceId || !effectiveGradeId) return;
      queryClient.setQueryData(
        schoolKeys.weeklyLearningGuideTrackColors(workspaceId, academicYear, effectiveGradeId),
        map
      );
    },
    [academicYear, effectiveGradeId, queryClient, workspaceId]
  );

  const termByNumber = React.useMemo(
    () => ({
      1: terms.find((term) => term.term_number === 1) ?? null,
      2: terms.find((term) => term.term_number === 2) ?? null,
    }),
    [terms]
  );

  /** 교시 칸 시각 기준 요일: 기본은 오늘, 수업 블럭을 클릭하면 그 요일 */
  const [periodTimeWeekdayKey, setPeriodTimeWeekdayKey] = React.useState<AnnualWeekdayKey | null>(null);

  /** 모든 템플릿 행(수업 외 방과후·활동 포함) 교시 칸 — 수업 행만 `periodSlots` */
  const allRowPeriodSlots = React.useMemo<(PeriodSlot & { isClassRow: boolean })[]>(() => {
    if (!schedule) return [];
    const slots: (PeriodSlot & { isClassRow: boolean })[] = [];
    let classOrder = 0;
    let afterSchoolOrder = 0;
    /** 요일별 시간표면 오늘 요일 시각 기준(주말이면 공통 시각) */
    const todayWeekdayKey = (['mon', 'tue', 'wed', 'thu', 'fri'] as const)[new Date().getDay() - 1] ?? null;
    const timeWeekdayKey =
      periodTimeWeekdayKey && isWeekdayTimeKey(periodTimeWeekdayKey) ? periodTimeWeekdayKey : todayWeekdayKey;
    const scheduleSettings = classScheduleSettings ?? loadClassScheduleSettingsCache(workspaceId);
    const timeRows = scheduleSettings
      ? resolveClassScheduleRowsForWeekday(scheduleSettings, schedule.sectionId, timeWeekdayKey)
      : schedule.sectionRows;
    schedule.sectionRows.forEach((baseRow, slotIndex) => {
      const isClassRow = baseRow.kind === 'class';
      let label: string;
      if (isClassRow) {
        classOrder += 1;
        label = `${classOrder}교시`;
      } else if (baseRow.kind === 'after_school') {
        afterSchoolOrder += 1;
        label = (baseRow.name ?? '').trim() || `방과후${afterSchoolOrder}`;
      } else {
        label = (baseRow.name ?? '').trim() || (baseRow.kind === 'activity' ? '활동' : `${slotIndex + 1}타임`);
      }
      const row = timeRows[slotIndex] ?? baseRow;
      const startTime = normalizePeriodTime(row.startTime);
      slots.push({
        slotIndex,
        label,
        startTime,
        endTime: periodEndTime(startTime, row.endTime, row.durationMinutes),
        isClassRow,
      });
    });
    return slots;
  }, [classScheduleSettings, periodTimeWeekdayKey, schedule, workspaceId]);

  const periodSlots = React.useMemo<PeriodSlot[]>(
    () =>
      allRowPeriodSlots
        .filter((slot) => slot.isClassRow)
        .map(({ isClassRow: _isClassRow, ...slot }) => slot),
    [allRowPeriodSlots]
  );

  const annualMapByTerm = React.useMemo<TermMap<Record<string, AnnualTimetableCell>>>(
    () => ({
      1: buildAnnualMap(annualRowsByTerm[1]),
      2: buildAnnualMap(annualRowsByTerm[2]),
    }),
    [annualRowsByTerm]
  );

  const progressMapByTerm = React.useMemo<TermMap<Record<string, ProgramProgressCell>>>(
    () => ({
      1: buildProgressMap(progressRowsByTerm[1]),
      2: buildProgressMap(progressRowsByTerm[2]),
    }),
    [progressRowsByTerm]
  );

  const noteMapByTerm = React.useMemo<TermMap<Record<string, WeeklyLearningGuideNote>>>(
    () => ({
      1: buildNoteMap(savedNotesByTerm[1]),
      2: buildNoteMap(savedNotesByTerm[2]),
    }),
    [savedNotesByTerm]
  );

  const trackNameByTimetableTextByGrade = React.useMemo(
    () => buildTrackNameByTimetableTextByGrade(tracks),
    [tracks]
  );

  const otherTrackLabelsForGrade = React.useMemo(
    () => collectOtherTrackLabelsForGrade(tracks, effectiveGradeId),
    [effectiveGradeId, tracks]
  );

  const orderedTrackNamesForGrade = React.useMemo(
    () => getOrderedTrackNamesForGrade(workspaceId, effectiveGradeId, tracks),
    [effectiveGradeId, workspaceId, tracks]
  );

  const trackPaletteBgByTrackName = React.useMemo(
    () => mergeWeeklyGuideTrackColorMap(orderedTrackNamesForGrade, savedTrackColorMap),
    [orderedTrackNamesForGrade, savedTrackColorMap]
  );
  const savedColorMode = React.useMemo(() => readWeeklyGuideColorMode(savedTrackColorMap), [savedTrackColorMap]);

  const scopedRowsIndexByTerm = React.useMemo<TermMap<ScopedRowsAnnualIndex>>(
    () => ({
      1: buildScopedRowsAnnualIndex(scopedRowsByTerm[1]),
      2: buildScopedRowsAnnualIndex(scopedRowsByTerm[2]),
    }),
    [scopedRowsByTerm]
  );

  const fixedContentByEventByTerm = React.useMemo<TermMap<Record<string, ProgramFixedContentRow>>>(
    () => ({
      1: fixedContentRowsByTerm[1].reduce<Record<string, ProgramFixedContentRow>>((acc, row) => {
        const eventName = eventNameFromFixedContentRow(row);
        if (eventName && !acc[eventName]) acc[eventName] = row;
        return acc;
      }, {}),
      2: fixedContentRowsByTerm[2].reduce<Record<string, ProgramFixedContentRow>>((acc, row) => {
        const eventName = eventNameFromFixedContentRow(row);
        if (eventName && !acc[eventName]) acc[eventName] = row;
        return acc;
      }, {}),
    }),
    [fixedContentRowsByTerm]
  );

  const calendarDayByDateByTerm = React.useMemo<TermMap<Record<string, CalendarDay>>>(
    () => ({
      1: calendarDaysByTerm[1].reduce<Record<string, CalendarDay>>((acc, row) => {
        acc[row.day] = row;
        return acc;
      }, {}),
      2: calendarDaysByTerm[2].reduce<Record<string, CalendarDay>>((acc, row) => {
        acc[row.day] = row;
        return acc;
      }, {}),
    }),
    [calendarDaysByTerm]
  );

  const getEffectiveSlotKind = React.useCallback(
    (termNumber: TermNumber, mondayIso: string, weekdayKey: AnnualWeekdayKey, dateIso: string, slotIndex: number) => {
      if (!schedule) return 'out_of_term';
      const term = termByNumber[termNumber];
      if (!term) return 'out_of_term';
      const storedCell =
        annualMapByTerm[termNumber][buildAnnualCellKey(effectiveGradeId, mondayIso, weekdayKey, slotIndex)];
      return (
        storedCell?.slot_kind ??
        getSeedSlotKind({
          dateIso,
          weekdayKey,
          slotIndex,
          sectionRows: schedule.sectionRows,
          slotLimitByDay: schedule.slotLimitByDay,
          startsOn: term.starts_on,
          endsOn: term.ends_on,
          holidaySet: nonClassHolidaySetByTerm[termNumber],
          calendarDayKindByDate: calendarDayKindByTerm[termNumber],
        })
      );
    },
    [annualMapByTerm, calendarDayKindByTerm, effectiveGradeId, nonClassHolidaySetByTerm, schedule, termByNumber]
  );

  const getActualClassCountForDay = React.useCallback(
    (termNumber: TermNumber, mondayIso: string, weekdayKey: AnnualWeekdayKey, dateIso: string) =>
      periodSlots.reduce((count, slot) => {
        const slotKind = getEffectiveSlotKind(termNumber, mondayIso, weekdayKey, dateIso, slot.slotIndex);
        return slotKind === 'class' ? count + 1 : count;
      }, 0),
    [getEffectiveSlotKind, periodSlots]
  );

  const getExpectedClassCountForDay = React.useCallback(
    (termNumber: TermNumber, mondayIso: string, weekdayKey: AnnualWeekdayKey, dateIso: string) => {
      if (!schedule) return 0;
      const term = termByNumber[termNumber];
      if (!term) return 0;
      return periodSlots.reduce((count, slot) => {
        const expectedKind = getSeedSlotKind({
          dateIso,
          weekdayKey,
          slotIndex: slot.slotIndex,
          sectionRows: schedule.sectionRows,
          slotLimitByDay: schedule.slotLimitByDay,
          startsOn: term.starts_on,
          endsOn: term.ends_on,
          holidaySet: nonClassHolidaySetByTerm[termNumber],
          calendarDayKindByDate: calendarDayKindByTerm[termNumber],
        });
        return expectedKind === 'class' ? count + 1 : count;
      }, 0);
    },
    [calendarDayKindByTerm, nonClassHolidaySetByTerm, periodSlots, schedule, termByNumber]
  );

  const weeksByTerm = React.useMemo<TermMap<WeekInfo[]>>(() => {
    const base: TermMap<WeekInfo[]> = { 1: [], 2: [] };
    ([1, 2] as TermNumber[]).forEach((termNumber) => {
      const term = termByNumber[termNumber];
      if (!term || !schedule) return;
      base[termNumber] = buildWeeksFromRange(term.starts_on, term.ends_on).filter((week) =>
        ANNUAL_WEEKDAY_KEYS.some(({ key }, index) => {
          const dateIso = week.datesByWeekday[index] ?? '';
          if (!dateIso || dateIso < term.starts_on || dateIso > term.ends_on) return false;
          return getActualClassCountForDay(termNumber, week.mondayIso, key, dateIso) > 0;
        })
      );
    });
    return base;
  }, [getActualClassCountForDay, schedule, termByNumber]);

  const availableTerms = React.useMemo(
    () => ([1, 2] as TermNumber[]).filter((termNumber) => weeksByTerm[termNumber].length > 0),
    [weeksByTerm]
  );

  React.useEffect(() => {
    const initKey = `${workspaceId}|${academicYear}|${effectiveGradeId}`;
    const openingPending = weeklyGuideOpenPickRef.current !== initKey;

    if (openingPending && !availableTerms.length) {
      return;
    }

    const todayIso = toISODate(new Date());

    if (openingPending && availableTerms.length) {
      weeklyGuideOpenPickRef.current = initKey;
      const preferredTerm = preferredTermForToday(terms, todayIso, availableTerms);
      const vt: TermNumber =
        termFromUrl != null && availableTerms.includes(termFromUrl)
          ? termFromUrl
          : preferredTerm ?? availableTerms[0] ?? 1;
      const vm =
        pickDefaultWeekMondayIso(weeksByTerm[vt], todayIso) || weeksByTerm[vt][0]?.mondayIso || '';
      setTermSelection(vt);
      setSelectedWeekByTerm({
        1: pickDefaultWeekMondayIso(weeksByTerm[1], todayIso),
        2: pickDefaultWeekMondayIso(weeksByTerm[2], todayIso),
      });
      if (vm) {
        setViewedTerm(vt);
        setViewedMondayIso(vm);
      }
      return;
    }

    if (!availableTerms.includes(termSelection)) {
      setTermSelection(availableTerms[0] ?? 1);
    }
    setSelectedWeekByTerm((prev) => {
      const next = { ...prev };
      let changed = false;
      ([1, 2] as TermNumber[]).forEach((termNumber) => {
        const list = weeksByTerm[termNumber];
        const defaultMon = pickDefaultWeekMondayIso(list, todayIso);
        const hasCurrent = list.some((week) => week.mondayIso === prev[termNumber]);
        if (!hasCurrent && prev[termNumber] !== defaultMon) {
          next[termNumber] = defaultMon;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [
    academicYear,
    availableTerms,
    effectiveGradeId,
    termFromUrl,
    termSelection,
    terms,
    weeksByTerm,
    workspaceId,
  ]);

  React.useEffect(() => {
    if (viewedTerm && weeksByTerm[viewedTerm].some((week) => week.mondayIso === viewedMondayIso)) return;
    const todayIso = toISODate(new Date());
    if (!availableTerms.length) {
      setViewedTerm(null);
      setViewedMondayIso('');
      return;
    }
    const nextTerm =
      viewedTerm && availableTerms.includes(viewedTerm)
        ? viewedTerm
        : preferredTermForToday(terms, todayIso, availableTerms) ?? availableTerms[0] ?? 1;
    const list = weeksByTerm[nextTerm];
    if (!list.length) {
      setViewedTerm(null);
      setViewedMondayIso('');
      return;
    }
    const raw = selectedWeekByTerm[nextTerm];
    const mon =
      raw && list.some((w) => w.mondayIso === raw)
        ? raw
        : pickDefaultWeekMondayIso(list, todayIso) || list[0]?.mondayIso || '';
    setViewedTerm(nextTerm);
    setViewedMondayIso(mon);
  }, [availableTerms, selectedWeekByTerm, terms, viewedMondayIso, viewedTerm, weeksByTerm]);

  const fixedEventsByTerm = React.useMemo<TermMap<Record<string, FixedEventSummary[]>>>(
    () => ({
      1: annualRowsByTerm[1].reduce<Record<string, FixedEventSummary[]>>((acc, row) => {
        const eventName = (row.memo ?? '').trim();
        if (!eventName || isAnnualShortDayTailMemo(eventName) || !isFixedProgramColor(row.color_hex ?? '', regularColors)) {
          return acc;
        }
        const dateIso = dateIsoForMondayWeekday(row.monday_iso, row.weekday_key);
        const group = acc[row.monday_iso] ?? [];
        if (!group.some((item) => item.dateIso === dateIso && item.eventName === eventName)) {
          group.push({
            dateIso,
            mondayIso: row.monday_iso,
            weekdayKey: row.weekday_key,
            eventName,
          });
          group.sort((a, b) => a.dateIso.localeCompare(b.dateIso) || a.eventName.localeCompare(b.eventName, 'ko'));
          acc[row.monday_iso] = group;
        }
        return acc;
      }, {}),
      2: annualRowsByTerm[2].reduce<Record<string, FixedEventSummary[]>>((acc, row) => {
        const eventName = (row.memo ?? '').trim();
        if (!eventName || isAnnualShortDayTailMemo(eventName) || !isFixedProgramColor(row.color_hex ?? '', regularColors)) {
          return acc;
        }
        const dateIso = dateIsoForMondayWeekday(row.monday_iso, row.weekday_key);
        const group = acc[row.monday_iso] ?? [];
        if (!group.some((item) => item.dateIso === dateIso && item.eventName === eventName)) {
          group.push({
            dateIso,
            mondayIso: row.monday_iso,
            weekdayKey: row.weekday_key,
            eventName,
          });
          group.sort((a, b) => a.dateIso.localeCompare(b.dateIso) || a.eventName.localeCompare(b.eventName, 'ko'));
          acc[row.monday_iso] = group;
        }
        return acc;
      }, {}),
    }),
    [annualRowsByTerm, regularColors]
  );

  const defaultNoticeByTerm = React.useMemo<TermMap<Record<string, string>>>(() => {
    const base: TermMap<Record<string, string>> = { 1: {}, 2: {} };
    ([1, 2] as TermNumber[]).forEach((termNumber) => {
      const term = termByNumber[termNumber];
      if (!term) return;
      weeksByTerm[termNumber].forEach((week) => {
        const lines: string[] = [];
        const seen = new Set<string>();
        ANNUAL_WEEKDAY_KEYS.forEach(({ key, label }, index) => {
          const dateIso = week.datesByWeekday[index] ?? '';
          if (!dateIso || dateIso < term.starts_on || dateIso > term.ends_on) return;
          const calendarDay = calendarDayByDateByTerm[termNumber][dateIso];
          if (calendarDay && calendarDay.day_kind !== 'class') {
            const name =
              (calendarDay.note ?? '').trim() ||
              (calendarDay.day_kind === 'holiday'
                ? '공휴일'
                : calendarDay.day_kind === 'vacation'
                  ? '방학'
                  : '휴업일');
            const line = `${formatActivityDate(dateIso, label)} ${name}`;
            if (!seen.has(line)) {
              seen.add(line);
              lines.push(line);
            }
          }
          const expectedClassCount = getExpectedClassCountForDay(termNumber, week.mondayIso, key, dateIso);
          const actualClassCount = getActualClassCountForDay(termNumber, week.mondayIso, key, dateIso);
          if (expectedClassCount !== actualClassCount && actualClassCount > 0) {
            const targetLabel = `${actualClassCount}교시`;
            const line = `${formatActivityDate(dateIso, label)} ${
              actualClassCount > expectedClassCount ? `${targetLabel}로 증배` : `${targetLabel}로 단축`
            }`;
            if (!seen.has(line)) {
              seen.add(line);
              lines.push(line);
            }
          }
        });
        (fixedEventsByTerm[termNumber][week.mondayIso] ?? []).forEach((event) => {
          const weekdayLabel = ANNUAL_WEEKDAY_KEYS.find((candidate) => candidate.key === event.weekdayKey)?.label ?? '';
          const line = `${formatActivityDate(event.dateIso, weekdayLabel)} ${event.eventName}`;
          if (!seen.has(line)) {
            seen.add(line);
            lines.push(line);
          }
        });
        base[termNumber][week.mondayIso] = lines.join('\n');
      });
    });
    return base;
  }, [
    calendarDayByDateByTerm,
    fixedEventsByTerm,
    getActualClassCountForDay,
    getExpectedClassCountForDay,
    termByNumber,
    weeksByTerm,
  ]);

  const viewedWeek = React.useMemo(
    () => (viewedTerm ? weeksByTerm[viewedTerm].find((week) => week.mondayIso === viewedMondayIso) ?? null : null),
    [viewedMondayIso, viewedTerm, weeksByTerm]
  );

  const viewedColumns = React.useMemo<WeeklyDayColumn[]>(() => {
    if (!viewedWeek || !viewedTerm) return [];
    const term = termByNumber[viewedTerm];
    if (!term) return [];
    const baseColumns = ANNUAL_WEEKDAY_KEYS.slice(0, 5).map((weekday, index) => {
      const dateIso = viewedWeek.datesByWeekday[index] ?? '';
      return {
        weekdayKey: weekday.key,
        weekdayLabel: weekday.label,
        dateIso,
        inTerm: !!dateIso && dateIso >= term.starts_on && dateIso <= term.ends_on,
      };
    });
    const satDateIso = viewedWeek.datesByWeekday[5] ?? '';
    const satInTerm = !!satDateIso && satDateIso >= term.starts_on && satDateIso <= term.ends_on;
    if (
      satInTerm &&
      getActualClassCountForDay(viewedTerm, viewedWeek.mondayIso, 'sat', satDateIso) > 0
    ) {
      baseColumns.push({
        weekdayKey: 'sat',
        weekdayLabel: '토',
        dateIso: satDateIso,
        inTerm: true,
      });
    }
    return baseColumns;
  }, [getActualClassCountForDay, termByNumber, viewedTerm, viewedWeek]);

  const lessonSectionBundle = React.useMemo((): DashboardLessonSectionBundle | null => {
    const scheduleSettings = classScheduleSettings ?? loadClassScheduleSettingsCache(workspaceId);
    if (!scheduleSettings) return null;
    const termData = (termNumber: TermNumber) => ({
      termNumber,
      annualRows: annualRowsByTerm[termNumber],
      progressRows: progressRowsByTerm[termNumber],
      scopedRows: scopedRowsByTerm[termNumber],
      fixedRows: fixedContentRowsByTerm[termNumber],
      holidayDays: Array.from(nonClassHolidaySetByTerm[termNumber]),
      offDayByDate: offDayByDateByTerm[termNumber],
      dayKindByDate: calendarDayKindByTerm[termNumber],
    });
    return {
      academicYear,
      terms,
      schedule: scheduleSettings,
      tracks,
      regularColors,
      dedicatedColors,
      baseExcludeColors,
      annualProjectPrograms,
      gradeTrackSections: loadGradeTrackSectionsCache(workspaceId) ?? [],
      byTerm: { 1: termData(1), 2: termData(2) },
    };
  }, [
    academicYear,
    annualProjectPrograms,
    annualRowsByTerm,
    baseExcludeColors,
    calendarDayKindByTerm,
    classScheduleSettings,
    dedicatedColors,
    fixedContentRowsByTerm,
    nonClassHolidaySetByTerm,
    offDayByDateByTerm,
    progressRowsByTerm,
    regularColors,
    scopedRowsByTerm,
    terms,
    tracks,
    workspaceId,
  ]);

  const embeddedLessonBundle = embedded?.lessonBundle ?? null;
  const hostLessonBundle =
    embeddedLessonBundle && embeddedLessonBundle.academicYear === academicYear ? embeddedLessonBundle : null;

  const slotsByWeekday = React.useMemo(() => {
    const map: Partial<Record<AnnualWeekdayKey, DashboardAnnualDaySlot[]>> = {};
    if (!workspaceId || !effectiveGradeId || !viewedTerm || !viewedWeek) return map;
    viewedColumns.forEach((column) => {
      map[column.weekdayKey] = buildDashboardWeeklyDaySlots({
        workspaceId,
        gradeId: effectiveGradeId,
        grades,
        mondayIso: viewedWeek.mondayIso,
        weekdayKey: column.weekdayKey,
        dateIso: column.dateIso,
        academicYear,
        termNumber: viewedTerm,
        bundle: hostLessonBundle ?? lessonSectionBundle,
      });
    });
    return map;
  }, [
    academicYear,
    effectiveGradeId,
    grades,
    hostLessonBundle,
    lessonSectionBundle,
    viewedColumns,
    viewedTerm,
    viewedWeek,
    workspaceId,
  ]);

  /** 대시보드 시간표: 공휴일 외 날에 편제가 있는 방과후·활동 행도 교시 칸으로 추가 */
  const displayPeriodSlots = React.useMemo<PeriodSlot[]>(() => {
    if (!embedded) return periodSlots;
    const extraSlotIndexes = new Set<number>();
    Object.values(slotsByWeekday).forEach((daySlots) => {
      daySlots?.forEach((slot) => {
        if (slot.timetableSlotKind === 'holiday' || slot.timetableSlotKind === 'out_of_term') return;
        if (!annualSlotHasTimetableProgramAssignment((slot.timetableText ?? '').trim(), slot.timetableColorHex)) return;
        extraSlotIndexes.add(slot.slotIndex);
      });
    });
    return allRowPeriodSlots
      .filter((slot) => slot.isClassRow || extraSlotIndexes.has(slot.slotIndex))
      .map(({ isClassRow: _isClassRow, ...slot }) => slot);
  }, [allRowPeriodSlots, embedded, periodSlots, slotsByWeekday]);

  const lessonSlotRecords = useDashboardLessonSlotRecords({
    workspaceId,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber: viewedTerm ?? termSelection,
  });

  React.useEffect(() => {
    setLessonMenu(null);
  }, [viewedMondayIso, viewedTerm, neisClassTimetableMode]);

  const selectedPeriodKey = lessonMenu
    ? `${lessonMenu.slot.weekdayKey}:${lessonMenu.slot.slotIndex}`
    : '';

  const openPeriodLessonMenu = React.useCallback(
    (column: WeeklyDayColumn, slotIndex: number, anchorEl: HTMLElement) => {
      const slots = slotsByWeekday[column.weekdayKey] ?? [];
      const slot = slots.find((row) => row.slotIndex === slotIndex);
      if (!slot) return;
      setPeriodTimeWeekdayKey(column.weekdayKey);
      setLessonMenu((prev) => {
        if (prev && prev.slot.weekdayKey === column.weekdayKey && prev.slot.slotIndex === slotIndex) {
          return null;
        }
        return { slot, rect: weeklyGuideCardRect(anchorEl, column.weekdayKey) };
      });
    },
    [slotsByWeekday]
  );

  const periodCellSelectableProps = React.useCallback(
    (column: WeeklyDayColumn, period: PeriodSlot, extraClass = '') => {
      const selected = selectedPeriodKey === `${column.weekdayKey}:${period.slotIndex}`;
      return {
        role: 'button' as const,
        tabIndex: 0,
        'data-wlg-day': column.weekdayKey,
        className: [
          extraClass,
          'cp-weekly-learning-guide-cell--period-selectable',
          selected ? 'is-period-selected' : '',
        ]
          .filter(Boolean)
          .join(' '),
        'aria-pressed': selected,
        'aria-label': `${period.label} ${column.weekdayLabel} 수업`,
        onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
          e.preventDefault();
          openPeriodLessonMenu(column, period.slotIndex, e.currentTarget);
        },
        onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          openPeriodLessonMenu(column, period.slotIndex, e.currentTarget);
        },
      };
    },
    [openPeriodLessonMenu, selectedPeriodKey]
  );

  const openWeeklyGuideSlotPresentation = React.useCallback(
    (slot: DashboardAnnualDaySlot) => {
      if (!workspaceId?.trim() || !effectiveGradeId?.trim()) return;
      if (
        slot.timetableSlotKind === 'out_of_term' ||
        slot.timetableSlotKind === 'holiday' ||
        !annualSlotHasTimetableProgramAssignment(slot.timetableText, slot.timetableColorHex)
      ) {
        return;
      }
      openDashboardAnnualSlotPresentation({
        slot,
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear: slot.academicYear || academicYear,
        termNumber: (slot.termNumber ?? viewedTerm ?? 1) as 1 | 2,
      });
    },
    [academicYear, effectiveGradeId, viewedTerm, workspaceId]
  );

  const navigate = useNavigate();
  /** 연간진도표 '선택셀 프로그램 탐색'과 같은 경로로 이동 */
  const openWeeklyGuideSlotProgram = React.useCallback(
    (slot: DashboardAnnualDaySlot) => {
      const path = buildDashboardSlotProgramPath(slot, {
        gradeId: effectiveGradeId,
        academicYear,
        fallbackTermNumber: viewedTerm as 1 | 2 | null,
        annualProjectPrograms,
      });
      if (path) navigate(path);
    },
    [academicYear, annualProjectPrograms, effectiveGradeId, navigate, viewedTerm]
  );

  /** 연간진도표·대시보드와 같은 규칙: 편제/프로젝트 스코프에서 이 칸이 몇 번째 수업인지 세는 키 */
  const trackProgramModeByTrack = React.useMemo(
    () => (workspaceId && effectiveGradeId ? loadTrackProgramModeMap(workspaceId, effectiveGradeId) : {}),
    [effectiveGradeId, workspaceId]
  );
  const trackProgramSyncRepByTrack = React.useMemo(
    () => (workspaceId && effectiveGradeId ? loadTrackProgramSyncRepMap(workspaceId, effectiveGradeId) : {}),
    [effectiveGradeId, workspaceId]
  );
  const weeklyGuideScopedOccurrenceForSlot = React.useCallback(
    (
      termNumber: TermNumber,
      annualCellKey: string,
      effective: { textValue?: string | null; colorHex?: string | null; isBaseExcluded?: boolean }
    ) => {
      const timetableText = (effective.textValue ?? '').trim();
      const colorHex = (effective.colorHex ?? '').trim();
      const templateLabel = timetableText
        ? trackNameByTimetableTextByGrade[effectiveGradeId]?.[timetableText] ?? ''
        : '';
      const isProjectProgram = matchesAnnualProjectProgramCell(
        colorHex,
        annualProjectPrograms,
        termNumber,
        effective.isBaseExcluded
      );
      const isFixedSlot = !isProjectProgram && isFixedProgramColor(colorHex, regularColors);
      const programKindLabel = isProjectProgram
        ? '프로'
        : isFixedSlot
          ? '고정'
          : isDedicatedColor(colorHex, dedicatedColors)
            ? '전담'
            : '';
      const projectId =
        programKindLabel === '프로'
          ? annualProjectPrograms.find(
              (program) =>
                program.termNumber === termNumber && program.colorHex.trim().toLowerCase() === colorHex.toLowerCase()
            )?.id ?? null
          : null;
      const legacy = progressMapByTerm[termNumber][annualCellKey];
      const legacyTimetableText = (legacy?.timetable_text ?? '').trim();
      const scopeLookupAlias =
        legacyTimetableText && (!timetableText || legacyTimetableText === timetableText) ? legacyTimetableText : timetableText;
      return {
        programKindLabel,
        isFixedSlot,
        projectId,
        templateLabel,
        ...computeScopedOccurrenceForAnnualSlot({
          isFixedSlot,
          programKindLabel,
          projectId,
          templateLabel,
          scopeLookupAlias,
          trackProgramModeByTrack,
          trackProgramSyncRepByTrack,
        }),
      };
    },
    [
      annualProjectPrograms,
      dedicatedColors,
      effectiveGradeId,
      progressMapByTerm,
      regularColors,
      trackNameByTimetableTextByGrade,
      trackProgramModeByTrack,
      trackProgramSyncRepByTrack,
    ]
  );

  const trackOccurrenceIndexByAnnualKeyByTerm = React.useMemo<TermMap<Record<string, number>>>(() => {
    const out: TermMap<Record<string, number>> = { 1: {}, 2: {} };
    if (!schedule || !effectiveGradeId) return out;

    ([1, 2] as TermNumber[]).forEach((termNumber) => {
      const term = termByNumber[termNumber];
      if (!term) return;
      const occurrenceByScopeKey = new Map<string, number>();
      weeksByTerm[termNumber].forEach((week) => {
        ANNUAL_WEEKDAY_KEYS.forEach(({ key }, index) => {
          const dateIso = week.datesByWeekday[index] ?? '';
          if (!dateIso || dateIso < term.starts_on || dateIso > term.ends_on) return;
          periodSlots.forEach((slot) => {
            const annualCellKey = buildAnnualCellKey(effectiveGradeId, week.mondayIso, key, slot.slotIndex);
            const annual = annualMapByTerm[termNumber][annualCellKey];
            const effective = deriveAnnualEffectiveCell({
              workspaceId,
              academicYear,
              termNumber,
              gradeId: effectiveGradeId,
              mondayIso: week.mondayIso,
              weekdayKey: key,
              slotIndex: slot.slotIndex,
              dateIso,
              sectionRows: schedule.sectionRows,
              slotLimitByDay: schedule.slotLimitByDay,
              startsOn: term.starts_on,
              endsOn: term.ends_on,
              holidaySet: nonClassHolidaySetByTerm[termNumber],
              storedCell: annual,
              dedicatedColors,
              baseExcludeColors: [],
              calendarDayKindByDate: calendarDayKindByTerm[termNumber],
            });
            if (effective.slotKind !== 'class') return;
            const occurrenceKey = weeklyGuideScopedOccurrenceForSlot(termNumber, annualCellKey, effective).scopedOccurrenceKey;
            if (!occurrenceKey) return;
            out[termNumber][annualCellKey] = occurrenceByScopeKey.get(occurrenceKey) ?? 0;
            occurrenceByScopeKey.set(occurrenceKey, (occurrenceByScopeKey.get(occurrenceKey) ?? 0) + 1);
          });
        });
      });
    });

    return out;
  }, [
    academicYear,
    annualMapByTerm,
    calendarDayKindByTerm,
    dedicatedColors,
    effectiveGradeId,
    nonClassHolidaySetByTerm,
    periodSlots,
    progressMapByTerm,
    schedule,
    workspaceId,
    termByNumber,
    trackNameByTimetableTextByGrade,
    weeklyGuideScopedOccurrenceForSlot,
    weeksByTerm,
  ]);

  const getCellDisplay = React.useCallback(
    (termNumber: TermNumber, mondayIso: string, weekdayKey: AnnualWeekdayKey, dateIso: string, slotIndex: number): WeeklyCellDisplay => {
      const emptyBase: Pick<WeeklyCellDisplay, 'subject' | 'unit' | 'learning' | 'page' | 'material'> = {
        subject: '',
        unit: '',
        learning: '',
        page: '',
        material: '',
      };
      if (!dateIso || !schedule) {
        return { ...emptyBase, slotSurface: 'non-class', neisShortIncreaseHighlight: false };
      }
      const term = termByNumber[termNumber];
      if (!term) {
        return { ...emptyBase, slotSurface: 'non-class', neisShortIncreaseHighlight: false };
      }
      const key = buildAnnualCellKey(effectiveGradeId, mondayIso, weekdayKey, slotIndex);
      const annual = annualMapByTerm[termNumber][key];
      const effective = deriveAnnualEffectiveCell({
        workspaceId,
        academicYear,
        termNumber,
        gradeId: effectiveGradeId,
        mondayIso,
        weekdayKey,
        slotIndex,
        dateIso,
        sectionRows: schedule.sectionRows,
        slotLimitByDay: schedule.slotLimitByDay,
        startsOn: term.starts_on,
        endsOn: term.ends_on,
        holidaySet: nonClassHolidaySetByTerm[termNumber],
        storedCell: annual,
        dedicatedColors,
        baseExcludeColors: [],
        calendarDayKindByDate: calendarDayKindByTerm[termNumber],
      });
      const baseGridByTerm = baseTimetableByGrade[effectiveGradeId];
      const baseGrid = getBaseTimetableGridForTerm(baseGridByTerm ?? null, termNumber as 1 | 2);
      const baseSlotIdx = baseGridClassPeriodIndexForSectionSlot(schedule.sectionRows, slotIndex);
      const baseCell = baseSlotIdx >= 0 ? getBaseGridCell(baseGrid, weekdayKey, baseSlotIdx) : null;
      const templateRowKind = schedule.sectionRows[slotIndex]?.kind;
      const adjustableTemplateRow = templateRowKind === 'class' || templateRowKind === 'after_school';
      const seedKind = getSeedSlotKind({
        dateIso,
        weekdayKey,
        slotIndex,
        sectionRows: schedule.sectionRows,
        slotLimitByDay: schedule.slotLimitByDay,
        startsOn: term.starts_on,
        endsOn: term.ends_on,
        holidaySet: nonClassHolidaySetByTerm[termNumber],
        calendarDayKindByDate: calendarDayKindByTerm[termNumber],
      });
      const actualKind = effective.slotKind;
      /** 연간시간표 `shortDaySummaries`와 동일 축: 시드가 수업/비수업인 교시에서 저장값만 class↔non_class로 달라질 때 */
      const neisShortIncreaseHighlight =
        adjustableTemplateRow &&
        (seedKind === 'class' || seedKind === 'non_class') &&
        (actualKind === 'class' || actualKind === 'non_class') &&
        seedKind !== actualKind;
      const slotSurface = weeklySlotSurfaceFromEffective(effective);

      if (
        embedded &&
        effective.slotKind !== 'class' &&
        effective.slotKind !== 'holiday' &&
        effective.slotKind !== 'out_of_term' &&
        viewedTerm === termNumber &&
        viewedWeek?.mondayIso === mondayIso
      ) {
        /** 대시보드 시간표 모드: 데일리·위클리와 같은 슬롯으로 활동·비수업 편제도 표시 */
        const dashSlot = slotsByWeekday[weekdayKey]?.find((row) => row.slotIndex === slotIndex);
        const dashText = (dashSlot?.timetableText ?? '').trim();
        if (dashSlot && annualSlotHasTimetableProgramAssignment(dashText, dashSlot.timetableColorHex)) {
          const dashTemplate = (dashSlot.templateLabel ?? '').trim();
          const dashHex = (dashSlot.timetableColorHex ?? '').trim();
          const d = dashSlot.display;
          const dashTrackBgHex =
            savedColorMode === 'cell'
              ? intensifyLightWeeklyGuideColorHex(dashHex) || WEEKLY_GUIDE_CELL_MODE_FALLBACK_HEX
              : dashTemplate
                ? trackPaletteBgByTrackName[dashTemplate]
                : undefined;
          return {
            subject: formatSubjectLabel(dashTemplate, dashText),
            subjectTitle: dashTemplate || dashText,
            subjectSubtitle: dashTemplate && dashText !== dashTemplate ? dashText : '',
            subjectTrackBgHex: dashTrackBgHex,
            unit: (d?.progressUnit ?? '').trim(),
            learning: (d?.progressLearning ?? '').trim(),
            page: formatPageText(d?.progressPage ?? '', d?.progressAssistantPage ?? ''),
            material: (d?.progressMaterial ?? '').trim(),
            neisTrackName: dashTemplate,
            neisDedicated: false,
            neisUnregisteredTimetableText: !dashTemplate && dashText ? dashText : undefined,
            slotSurface: 'class',
            neisShortIncreaseHighlight,
          };
        }
      }
      if (effective.slotKind !== 'class') {
        const subject =
          effective.slotKind === 'holiday'
            ? timetableTextForOffDayRow(dateIso, offDayByDateByTerm[termNumber])
            : '';
        return {
          ...emptyBase,
          subject,
          slotSurface,
          neisShortIncreaseHighlight,
        };
      }
      const progress = progressMapByTerm[termNumber][key];
      const timetableText = (effective.textValue ?? '').trim();
      const timetableColorHex = (effective.colorHex ?? '').trim();
      const timetableMemoRaw = isAnnualShortDayTailMemo(effective.memo) ? '' : (effective.memo ?? '').trim();
      const templateLabel = timetableText
        ? trackNameByTimetableTextByGrade[effectiveGradeId]?.[timetableText] ?? ''
        : '';
      if (!embedded && isWeeklyGuideHiddenOtherTrack(otherTrackLabelsForGrade, templateLabel, timetableText)) {
        return {
          ...emptyBase,
          slotSurface,
          neisShortIncreaseHighlight,
        };
      }
      const baseTimetableText = (baseCell?.name ?? '').trim();
      const baseColorHex = (baseCell?.colorHex ?? '').trim();
      const baseTemplate = baseTimetableText
        ? trackNameByTimetableTextByGrade[effectiveGradeId]?.[baseTimetableText] ?? ''
        : '';
      const neisVsBase = classifyNeisCellVsBase({
        annualTimetableText: timetableText,
        annualTemplate: templateLabel,
        annualColorHex: timetableColorHex,
        baseTimetableText,
        baseTemplate,
        baseColorHex,
        dedicatedColors,
      });
      const neisTrackName = (templateLabel || '').trim();
      const neisDedicated =
        slotSurface === 'class' &&
        !!neisTrackName &&
        isDedicatedColor(timetableColorHex, dedicatedColors);
      const neisUnregisteredTimetableText =
        !neisTrackName && timetableText.trim() ? timetableText.trim() : undefined;
      const isProjectProgram = matchesAnnualProjectProgramCell(
        timetableColorHex,
        annualProjectPrograms,
        termNumber,
        effective.isBaseExcluded
      );
      const isFixedSlot = !isProjectProgram && isFixedProgramColor(timetableColorHex, regularColors);
      const programKindLabel = (() => {
        if (isProjectProgram) return '프로';
        if (isFixedSlot) return '고정';
        if (isDedicatedColor(timetableColorHex, dedicatedColors)) return '전담';
        return '';
      })();
      const projectForRow =
        programKindLabel === '프로'
          ? annualProjectPrograms.find(
              (program) => program.termNumber === termNumber && program.colorHex.trim().toLowerCase() === timetableColorHex.toLowerCase()
            ) ?? null
          : null;
      const progressSource: ProgramProgressLikeRow | null = (() => {
        if (programKindLabel === '고정') {
          const eventKey = normalizeFixedEventName(timetableMemoRaw);
          return (progress ?? (eventKey ? fixedContentByEventByTerm[termNumber][eventKey] ?? null : null)) as ProgramProgressLikeRow | null;
        }
        /** 편제·프로젝트: 그 스코프의 N번째 내용 행(연간진도표·대시보드와 같은 공통 규칙) */
        const occ = weeklyGuideScopedOccurrenceForSlot(termNumber, key, effective);
        return resolveAnnualSlotProgressSource({
          scopedRowsIndex: scopedRowsIndexByTerm[termNumber],
          annualCellKey: key,
          programKindLabel,
          isFixedSlot: false,
          hasTimetableAssignment: annualSlotHasTimetableProgramAssignment(timetableText, timetableColorHex),
          templateLabel,
          displayText: timetableText,
          projectId: projectForRow?.id ?? null,
          legacyPc: progress,
          trackProgramModeByTrack,
          trackProgramSyncRepByTrack,
          scopedOccurrenceIndex: occ.scopedOccurrenceKey
            ? trackOccurrenceIndexByAnnualKeyByTerm[termNumber][key]
            : undefined,
          preferredTrackScopeKey: occ.preferredTrackScopeKey,
          scopeLookupKey: occ.scopeLookupKey,
        });
      })();
      const displayFields = mapProgressLikeToDisplayFields(progressSource);
      const trackNameForPalette = (templateLabel || '').trim();
      const subjectTrackBgHex =
        savedColorMode === 'cell'
          ? intensifyLightWeeklyGuideColorHex(timetableColorHex ?? '') ||
            ((timetableText || templateLabel || '').trim() ? WEEKLY_GUIDE_CELL_MODE_FALLBACK_HEX : undefined)
          : trackNameForPalette
            ? trackPaletteBgByTrackName[trackNameForPalette]
            : undefined;
      const subjectTimetableLabel = (timetableText || (progress?.timetable_text ?? '')).trim();
      const subjectTemplateLabel = (templateLabel || '').trim();
      if (embedded && viewedTerm === termNumber && viewedWeek?.mondayIso === mondayIso) {
        /** 대시보드 시간표: 단원·학습내용은 데일리·위클리와 같은 슬롯 값 */
        const dashDisplay = slotsByWeekday[weekdayKey]?.find((row) => row.slotIndex === slotIndex)?.display;
        if (dashDisplay) {
          displayFields.unit = (dashDisplay.progressUnit ?? '').trim();
          displayFields.learning = (dashDisplay.progressLearning ?? '').trim();
          displayFields.page = dashDisplay.progressPage ?? '';
          displayFields.assistantPage = dashDisplay.progressAssistantPage ?? '';
          displayFields.material = (dashDisplay.progressMaterial ?? '').trim();
        }
      }
      return {
        subject: formatSubjectLabel(templateLabel, timetableText || (progress?.timetable_text ?? '')),
        subjectTitle: subjectTemplateLabel || subjectTimetableLabel,
        subjectSubtitle:
          subjectTemplateLabel && subjectTimetableLabel !== subjectTemplateLabel ? subjectTimetableLabel : '',
        subjectTrackBgHex,
        unit: displayFields.unit,
        learning: displayFields.learning,
        page: formatPageText(displayFields.page, displayFields.assistantPage),
        material: displayFields.material,
        neisTrackName,
        neisDedicated,
        neisUnregisteredTimetableText,
        neisVsBase,
        slotSurface,
        neisShortIncreaseHighlight,
      };
    },
    [
      academicYear,
      annualMapByTerm,
      annualProjectPrograms,
      baseTimetableByGrade,
      calendarDayKindByTerm,
      dedicatedColors,
      effectiveGradeId,
      fixedContentByEventByTerm,
      nonClassHolidaySetByTerm,
      otherTrackLabelsForGrade,
      offDayByDateByTerm,
      progressMapByTerm,
      regularColors,
      schedule,
      workspaceId,
      scopedRowsIndexByTerm,
      termByNumber,
      trackOccurrenceIndexByAnnualKeyByTerm,
      trackProgramModeByTrack,
      trackProgramSyncRepByTrack,
      weeklyGuideScopedOccurrenceForSlot,
      trackNameByTimetableTextByGrade,
      trackPaletteBgByTrackName,
      savedColorMode,
      embedded,
      slotsByWeekday,
      viewedTerm,
      viewedWeek?.mondayIso,
    ]
  );

  const activeNoticeText = React.useMemo(() => {
    if (!viewedTerm || !viewedMondayIso) return '';
    const saved = noteMapByTerm[viewedTerm][viewedMondayIso];
    if (saved) return saved.note_text ?? '';
    return defaultNoticeByTerm[viewedTerm][viewedMondayIso] ?? '';
  }, [defaultNoticeByTerm, noteMapByTerm, viewedMondayIso, viewedTerm]);

  React.useEffect(() => {
    if (!viewedTerm || !viewedMondayIso) {
      setInlineNoticeText('');
      setInlineNoticeError(null);
      return;
    }
    setInlineNoticeText(activeNoticeText);
    setInlineNoticeError(null);
  }, [activeNoticeText, viewedMondayIso, viewedTerm]);

  const persistInlineNoticeIfChanged = React.useCallback(async () => {
    if (!workspaceId || !effectiveGradeId || viewedTerm === null || !viewedMondayIso) return;
    const defaultT = defaultNoticeByTerm[viewedTerm]?.[viewedMondayIso] ?? '';
    const saved = noteMapByTerm[viewedTerm][viewedMondayIso];
    const persisted = saved ? (saved.note_text ?? '') : null;
    if (persisted === null && inlineNoticeText === defaultT) return;
    if (persisted !== null && persisted === inlineNoticeText) return;
    setInlineNoticeSaving(true);
    setInlineNoticeError(null);
    try {
      const savedRows = await upsertWeeklyLearningGuideNotes([
        {
          workspace_id: workspaceId,
          grade_id: effectiveGradeId,
          academic_year: academicYear,
          term_number: viewedTerm,
          monday_iso: viewedMondayIso,
          note_text: inlineNoticeText,
        },
      ]);
      updateWeeklyGuideNotesQueryCache(viewedTerm, savedRows);
    } catch (err) {
      setInlineNoticeError(err instanceof Error ? err.message : String(err));
    } finally {
      setInlineNoticeSaving(false);
    }
  }, [
    academicYear,
    defaultNoticeByTerm,
    effectiveGradeId,
    inlineNoticeText,
    noteMapByTerm,
    updateWeeklyGuideNotesQueryCache,
    workspaceId,
    viewedMondayIso,
    viewedTerm,
  ]);

  const openManageModal = () => {
    const nextDrafts: Record<string, string> = {};
    ([1, 2] as TermNumber[]).forEach((termNumber) => {
      weeksByTerm[termNumber].forEach((week) => {
        const key = `${termNumber}|${week.mondayIso}`;
        if (viewedTerm !== null && termNumber === viewedTerm && week.mondayIso === viewedMondayIso) {
          nextDrafts[key] = inlineNoticeText;
          return;
        }
        nextDrafts[key] =
          noteMapByTerm[termNumber][week.mondayIso]?.note_text ?? defaultNoticeByTerm[termNumber][week.mondayIso] ?? '';
      });
    });
    setDraftNotes(nextDrafts);
    setSaveError(null);
    setModalOpen(true);
  };

  const openTrackColorModal = React.useCallback(() => {
    setDraftTrackColorMap(mergeWeeklyGuideTrackColorMap(orderedTrackNamesForGrade, savedTrackColorMap));
    setDraftColorMode(readWeeklyGuideColorMode(savedTrackColorMap));
    setTrackColorModalError(null);
    setTrackColorResetConfirmOpen(false);
    setTrackColorModalOpen(true);
  }, [orderedTrackNamesForGrade, savedTrackColorMap]);

  const closeTrackColorModal = React.useCallback(() => {
    if (trackColorModalSaving) return;
    setTrackColorModalOpen(false);
    setTrackColorModalError(null);
    setTrackColorResetConfirmOpen(false);
  }, [trackColorModalSaving]);

  const saveTrackColorsFromModal = async () => {
    if (!workspaceId || !effectiveGradeId) return;
    setTrackColorModalSaving(true);
    setTrackColorModalError(null);
    try {
      const defaults = buildDefaultWeeklyGuideTrackColorMap(orderedTrackNamesForGrade);
      const payload: Record<string, string> = {};
      orderedTrackNamesForGrade.forEach((name) => {
        const k = name.trim();
        if (!k) return;
        const fromDraft = (draftTrackColorMap[k] ?? '').trim();
        payload[k] = fromDraft || (defaults[k] ?? '#FF5A5E');
      });
      payload[WEEKLY_GUIDE_COLOR_MODE_KEY] = draftColorMode;
      const saved = await upsertWeeklyLearningGuideTrackColors({
        workspace_id: workspaceId,
        grade_id: effectiveGradeId,
        academic_year: academicYear,
        track_color_map: payload,
      });
      updateWeeklyGuideTrackColorsQueryCache(saved.track_color_map);
      setTrackColorModalOpen(false);
      setTrackColorResetConfirmOpen(false);
    } catch (e) {
      setTrackColorModalError(e instanceof Error ? e.message : String(e));
    } finally {
      setTrackColorModalSaving(false);
    }
  };

  const persistDefaultTrackColors = async () => {
    if (!workspaceId || !effectiveGradeId) return;
    setTrackColorModalSaving(true);
    setTrackColorModalError(null);
    try {
      const defaults = buildDefaultWeeklyGuideTrackColorMap(orderedTrackNamesForGrade);
      const saved = await upsertWeeklyLearningGuideTrackColors({
        workspace_id: workspaceId,
        grade_id: effectiveGradeId,
        academic_year: academicYear,
        track_color_map: { ...defaults, [WEEKLY_GUIDE_COLOR_MODE_KEY]: draftColorMode },
      });
      updateWeeklyGuideTrackColorsQueryCache(saved.track_color_map);
      setDraftTrackColorMap(mergeWeeklyGuideTrackColorMap(orderedTrackNamesForGrade, saved.track_color_map));
      setTrackColorResetConfirmOpen(false);
    } catch (e) {
      setTrackColorModalError(e instanceof Error ? e.message : String(e));
    } finally {
      setTrackColorModalSaving(false);
    }
  };

  const saveManagedNotes = async () => {
    if (!effectiveGradeId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = ([1, 2] as TermNumber[]).flatMap((termNumber) =>
        weeksByTerm[termNumber].map((week) => ({
          workspace_id: workspaceId,
          grade_id: effectiveGradeId,
          academic_year: academicYear,
          term_number: termNumber,
          monday_iso: week.mondayIso,
          note_text: draftNotes[`${termNumber}|${week.mondayIso}`] ?? '',
        }))
      );
      const savedRows = await upsertWeeklyLearningGuideNotes(payload);
      ([1, 2] as TermNumber[]).forEach((termNumber) => {
        const rows = savedRows.filter((row) => row.term_number === termNumber);
        if (rows.length > 0) updateWeeklyGuideNotesQueryCache(termNumber, rows);
      });
      setModalOpen(false);
    } catch (saveErr) {
      setSaveError(saveErr instanceof Error ? saveErr.message : String(saveErr));
    } finally {
      setSaving(false);
    }
  };

  const navigateWeek = React.useCallback(
    (direction: 'prev' | 'next') => {
      if (loading) return;
      const list = weeksByTerm[termSelection];
      if (!list.length) return;
      let i = list.findIndex((w) => w.mondayIso === selectedWeekByTerm[termSelection]);
      if (i === -1) {
        if (direction === 'next' && list[0]) {
          const mon = list[0].mondayIso;
          setSelectedWeekByTerm((prev) => ({ ...prev, [termSelection]: mon }));
          setViewedTerm(termSelection);
          setViewedMondayIso(mon);
        }
        return;
      }
      const ni = direction === 'prev' ? i - 1 : i + 1;
      if (ni < 0 || ni >= list.length) return;
      const mon = list[ni].mondayIso;
      setSelectedWeekByTerm((prev) => ({ ...prev, [termSelection]: mon }));
      setViewedTerm(termSelection);
      setViewedMondayIso(mon);
    },
    [loading, selectedWeekByTerm, termSelection, weeksByTerm]
  );

  const goToTodayWeek = React.useCallback(() => {
    if (loading) return;
    const todayIso = toISODate(new Date());
    const term = preferredTermForToday(terms, todayIso, availableTerms) ?? termSelection;
    const mon = pickDefaultWeekMondayIso(weeksByTerm[term], todayIso);
    if (!mon) return;
    setTermSelection(term);
    setSelectedWeekByTerm((prev) => ({ ...prev, [term]: mon }));
    setViewedTerm(term);
    setViewedMondayIso(mon);
  }, [availableTerms, loading, termSelection, terms, weeksByTerm]);

  const weekNavList = weeksByTerm[termSelection];
  const weekNavIndex = weekNavList.findIndex((w) => w.mondayIso === selectedWeekByTerm[termSelection]);
  const weekNavCanPrev = !loading && weekNavList.length > 0 && weekNavIndex > 0;
  const weekNavCanNext =
    !loading && weekNavList.length > 0 && (weekNavIndex === -1 || weekNavIndex < weekNavList.length - 1);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (window.location.pathname !== (embedded ? '/dashboard' : '/weekly-learning-guide')) return;
      if (embedded?.keyboardDisabled) return;
      if (modalOpen || trackColorModalOpen) return;
      if (
        document.querySelector(
          '.cp-modal-backdrop, .cp-weekly-learning-guide-modal-backdrop, .cp-pin-confirm-overlay'
        )
      ) {
        return;
      }
      if (lessonMenu) setLessonMenu(null);
      if (e.target instanceof HTMLElement && e.target.closest('.cp-weekly-learning-guide-table')) {
        e.target.blur();
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!embedded) return;
        if (embeddedTodoMode) goToTodayWeek();
        else embedded.onTodoModeChange(true);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (embedded && embeddedTodoMode) embedded.onTodoModeChange(false);
        else goToTodayWeek();
        return;
      }
      if (e.key === 'ArrowLeft' ? !weekNavCanPrev : !weekNavCanNext) return;
      e.preventDefault();
      navigateWeek(e.key === 'ArrowLeft' ? 'prev' : 'next');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    embedded,
    embeddedTodoMode,
    goToTodayWeek,
    lessonMenu,
    modalOpen,
    navigateWeek,
    trackColorModalOpen,
    weekNavCanNext,
    weekNavCanPrev,
  ]);

  const onEmbeddedViewedWeekChange = embedded?.onViewedWeekChange;
  React.useEffect(() => {
    if (viewedMondayIso) onEmbeddedViewedWeekChange?.(viewedMondayIso);
  }, [onEmbeddedViewedWeekChange, viewedMondayIso]);

  const embeddedNavControlRef = embedded?.navControlRef;
  React.useEffect(() => {
    if (!embeddedNavControlRef) return;
    embeddedNavControlRef.current = {
      prev: () => {
        if (weekNavCanPrev) navigateWeek('prev');
      },
      next: () => {
        if (weekNavCanNext) navigateWeek('next');
      },
      today: goToTodayWeek,
    };
  }, [embeddedNavControlRef, goToTodayWeek, navigateWeek, weekNavCanNext, weekNavCanPrev]);
  React.useEffect(
    () => () => {
      if (embeddedNavControlRef) embeddedNavControlRef.current = null;
    },
    [embeddedNavControlRef]
  );

  /** 대시보드 시간표: 표 하단을 호스트 스크롤 영역 하단(위클리 하단)에 맞춤 — 6교시 기준, 그 이상은 스크롤 */
  const embeddedTableRef = React.useRef<HTMLTableElement | null>(null);
  const [embeddedFit, setEmbeddedFit] = React.useState<{ block: number; todo: number } | null>(null);
  const embeddedPeriodCount = displayPeriodSlots.length;
  const isEmbedded = Boolean(embedded);
  React.useLayoutEffect(() => {
    if (!isEmbedded) return;
    const table = embeddedTableRef.current;
    const host = table?.closest('.cp-dash-timetable-wrap') as HTMLElement | null;
    const thead = table?.tHead;
    if (!table || !host || !thead) return;
    const measure = () => {
      const hostRect = host.getBoundingClientRect();
      const hostStyle = getComputedStyle(host);
      /** 호스트 높이가 내용에 따라 늘어나는 레이아웃(모바일)에서 측정↔높이 증가 루프를 막기 위해 화면 하단으로 제한 */
      const viewportBottom = window.visualViewport?.height ?? window.innerHeight;
      const contentBottom = Math.min(hostRect.bottom - parseFloat(hostStyle.paddingBottom || '0'), viewportBottom);
      const theadBottom = thead.getBoundingClientRect().bottom + host.scrollTop;
      const tableWrap = table.parentElement;
      const wrapPadBottom = tableWrap ? parseFloat(getComputedStyle(tableWrap).paddingBottom || '0') : 0;
      const avail = contentBottom - theadBottom - wrapPadBottom;
      if (!Number.isFinite(avail) || avail <= 0) return;
      const divisor = Math.max(1, Math.min(6, embeddedPeriodCount || 6));
      const block = Math.max(96, Math.floor((avail / divisor) * 100) / 100);
      const todo = Math.max(200, Math.floor(avail - block * 0.05 - 1));
      setEmbeddedFit((prev) =>
        prev && Math.abs(prev.block - block) < 0.5 && Math.abs(prev.todo - todo) < 1 ? prev : { block, todo }
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    ro.observe(thead);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isEmbedded, embeddedPeriodCount, embeddedTodoMode, loading, viewedMondayIso]);

  const onEmbeddedNavStateChange = embedded?.onNavStateChange;
  const embeddedNavWeekIndex =
    weekNavList.find((w) => w.mondayIso === selectedWeekByTerm[termSelection])?.weekIndex ?? null;
  React.useEffect(() => {
    if (loading || embeddedNavWeekIndex == null) return;
    onEmbeddedNavStateChange?.({
      termNumber: termSelection ?? null,
      weekIndex: embeddedNavWeekIndex,
      canPrev: weekNavCanPrev,
      canNext: weekNavCanNext,
    });
  }, [
    embeddedNavWeekIndex,
    loading,
    onEmbeddedNavStateChange,
    termSelection,
    weekNavCanNext,
    weekNavCanPrev,
  ]);

  const canExportWeeklyGuide =
    !loading &&
    !!viewedWeek &&
    viewedTerm !== null &&
    viewedColumns.length > 0 &&
    periodSlots.length > 0;

  const canExportNeisTermWeeklyGuide =
    !loading &&
    neisClassTimetableMode &&
    periodSlots.length > 0 &&
    weeksByTerm[termSelection].length > 0;

  const buildExportColumnsForWeek = React.useCallback(
    (week: WeekInfo, termNumber: TermNumber): WeeklyDayColumn[] => {
      const term = termByNumber[termNumber];
      if (!term) return [];
      const baseColumns = ANNUAL_WEEKDAY_KEYS.slice(0, 5).map((weekday, index) => {
        const dateIso = week.datesByWeekday[index] ?? '';
        return {
          weekdayKey: weekday.key,
          weekdayLabel: weekday.label,
          dateIso,
          inTerm: !!dateIso && dateIso >= term.starts_on && dateIso <= term.ends_on,
        };
      });
      const satDateIso = week.datesByWeekday[5] ?? '';
      const satInTerm = !!satDateIso && satDateIso >= term.starts_on && satDateIso <= term.ends_on;
      if (
        satInTerm &&
        getActualClassCountForDay(termNumber, week.mondayIso, 'sat', satDateIso) > 0
      ) {
        baseColumns.push({
          weekdayKey: 'sat',
          weekdayLabel: '토',
          dateIso: satDateIso,
          inTerm: true,
        });
      }
      return baseColumns;
    },
    [getActualClassCountForDay, termByNumber]
  );

  const resolveNoticeTextForWeek = React.useCallback(
    (termNumber: TermNumber, mondayIso: string): string => {
      const saved = noteMapByTerm[termNumber][mondayIso];
      if (saved) return saved.note_text ?? '';
      if (viewedTerm === termNumber && mondayIso === viewedMondayIso) return inlineNoticeText;
      return defaultNoticeByTerm[termNumber][mondayIso] ?? '';
    },
    [defaultNoticeByTerm, inlineNoticeText, noteMapByTerm, viewedMondayIso, viewedTerm]
  );

  const buildWeeklyGuideWeekExportBlock = React.useCallback(
    (termNumber: TermNumber, week: WeekInfo): WeeklyLearningGuideExportWeekBlock | null => {
      const columns = buildExportColumnsForWeek(week, termNumber);
      if (columns.length === 0) return null;
      return {
        titleLine1: formatWeeklyGuideContainerTitle(termNumber, week),
        titleLine2: '',
        columns: columns.map((column) => ({
          weekdayLabel: column.weekdayLabel,
          dateHeader: column.inTerm ? formatDateHeader(column.dateIso) : '',
        })),
        periods: periodSlots.map((period) => ({
          label: period.label,
          cells: columns.map((column) => {
            const display = getCellDisplay(
              termNumber,
              week.mondayIso,
              column.weekdayKey,
              column.dateIso,
              period.slotIndex
            );
            return {
              subject: display.subject,
              subjectTitle: display.subjectTitle,
              subjectSubtitle: display.subjectSubtitle,
              subjectBgHex: display.subjectTrackBgHex,
              unit: display.unit,
              learning: display.learning,
              page: display.page,
              material: display.material,
              neisTrackName: display.neisTrackName,
              neisDedicated: display.neisDedicated,
              neisUnregisteredTimetableText: display.neisUnregisteredTimetableText,
              neisVsBase: display.neisVsBase,
              neisShortIncreaseHighlight: display.neisShortIncreaseHighlight,
              slotSurface: display.slotSurface,
            };
          }),
        })),
        noticeText: resolveNoticeTextForWeek(termNumber, week.mondayIso),
      };
    },
    [buildExportColumnsForWeek, getCellDisplay, periodSlots, resolveNoticeTextForWeek]
  );

  const weeklyGuideNeisTermWeekBlocks = React.useMemo((): WeeklyLearningGuideExportWeekBlock[] => {
    if (!canExportNeisTermWeeklyGuide) return [];
    return weeksByTerm[termSelection]
      .map((week) => buildWeeklyGuideWeekExportBlock(termSelection, week))
      .filter((block): block is WeeklyLearningGuideExportWeekBlock => block != null);
  }, [buildWeeklyGuideWeekExportBlock, canExportNeisTermWeeklyGuide, termSelection, weeksByTerm]);

  const weeklyGuideContainerTitle = React.useMemo(() => {
    if (!viewedWeek || viewedTerm === null) return '';
    return formatWeeklyGuideContainerTitle(viewedTerm, viewedWeek);
  }, [viewedTerm, viewedWeek]);

  const weeklyGuideContainerTitleParts = React.useMemo(() => {
    if (!viewedWeek || viewedTerm === null) return null;
    return formatWeeklyGuideContainerTitleParts(viewedTerm, viewedWeek);
  }, [viewedTerm, viewedWeek]);

  const weeklyGuideExportPayload = React.useMemo((): WeeklyLearningGuideExportPayload | null => {
    if (neisClassTimetableMode) {
      if (!canExportNeisTermWeeklyGuide || weeklyGuideNeisTermWeekBlocks.length === 0) return null;
      const first = weeklyGuideNeisTermWeekBlocks[0]!;
      return {
        titleLine1: `${termSelection}학기 NEIS 반별시간표 (전체 ${weeklyGuideNeisTermWeekBlocks.length}주)`,
        titleLine2: '',
        columns: first.columns,
        periods: first.periods,
        noticeText: '',
        neisMode: true,
        termWeekBlocks: weeklyGuideNeisTermWeekBlocks,
      };
    }

    if (!canExportWeeklyGuide || !viewedWeek || viewedTerm === null) return null;
    return {
      titleLine1: weeklyGuideContainerTitle,
      titleLine2: '',
      columns: viewedColumns.map((column) => ({
        weekdayLabel: column.weekdayLabel,
        dateHeader: column.inTerm ? formatDateHeader(column.dateIso) : '',
      })),
      periods: periodSlots.map((period) => ({
        label: period.label,
        cells: viewedColumns.map((column) => {
          const display = getCellDisplay(
            viewedTerm,
            viewedWeek.mondayIso,
            column.weekdayKey,
            column.dateIso,
            period.slotIndex
          );
          return {
            subject: display.subject,
            subjectTitle: display.subjectTitle,
            subjectSubtitle: display.subjectSubtitle,
            subjectBgHex: display.subjectTrackBgHex,
            unit: display.unit,
            learning: display.learning,
            page: display.page,
            material: display.material,
            neisTrackName: display.neisTrackName,
            neisUnregisteredTimetableText: display.neisUnregisteredTimetableText,
            slotSurface: display.slotSurface,
            ...(() => {
              if (display.slotSurface !== 'class') return {};
              const menuSlot = slotsByWeekday[column.weekdayKey]?.find((row) => row.slotIndex === period.slotIndex);
              const code = parseDashboardEvalStatusCode(menuSlot?.display.progressEvalCode);
              if (!code) return {};
              return {
                evalCode: code,
                evalLabel: (menuSlot?.display.progressEval ?? '').trim() || WEEKLY_GUIDE_EVAL_LABELS[code],
              };
            })(),
          };
        }),
      })),
      noticeText: inlineNoticeText,
      neisMode: false,
    };
  }, [
    canExportNeisTermWeeklyGuide,
    canExportWeeklyGuide,
    getCellDisplay,
    inlineNoticeText,
    neisClassTimetableMode,
    periodSlots,
    slotsByWeekday,
    termSelection,
    viewedColumns,
    viewedTerm,
    viewedWeek,
    weeklyGuideContainerTitle,
    weeklyGuideNeisTermWeekBlocks,
  ]);

  const buildWeeklyGuideExportFileBase = React.useCallback(() => {
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    const termPart = neisClassTimetableMode
      ? `${termSelection}학기`
      : viewedTerm !== null
        ? `${viewedTerm}학기`
        : '';
    const weekPart =
      neisClassTimetableMode && weeklyGuideNeisTermWeekBlocks.length > 0
        ? `전체${weeklyGuideNeisTermWeekBlocks.length}주`
        : viewedWeek
          ? `${viewedWeek.weekIndex}주`
          : '';
    const classPart = classParsed?.title
      ? classParsed.title.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48)
      : '클래스';
    const modePart = neisClassTimetableMode ? 'NEIS반별_' : '';
    return `주간학습안내_${modePart}${termPart}_${weekPart}_${classPart}_${dateLabel}`;
  }, [
    classParsed?.title,
    neisClassTimetableMode,
    termSelection,
    viewedTerm,
    viewedWeek,
    weeklyGuideNeisTermWeekBlocks.length,
  ]);

  const canExportWeeklyGuideDocument =
    !notesLoading &&
    (neisClassTimetableMode
      ? canExportNeisTermWeeklyGuide && weeklyGuideNeisTermWeekBlocks.length > 0
      : canExportWeeklyGuide);

  const handleWeeklyGuideSaveExcel = React.useCallback(async () => {
    if (!weeklyGuideExportPayload) return;
    setWeeklyGuideExportBusy(true);
    try {
      await saveWeeklyLearningGuideAsExcel(weeklyGuideExportPayload, `${buildWeeklyGuideExportFileBase()}.xlsx`);
    } catch (exportErr) {
      console.error('주간학습 안내 엑셀 저장 실패:', exportErr);
      alert('엑셀 저장에 실패했습니다.');
    } finally {
      setWeeklyGuideExportBusy(false);
    }
  }, [buildWeeklyGuideExportFileBase, weeklyGuideExportPayload]);

  const openWeeklyGuideA4PreviewTab = React.useCallback(
    (action: 'pdf' | 'print') => {
      if (!weeklyGuideExportPayload) return;
      const placeholder = openWeeklyLearningGuideA4PreviewTabPlaceholder();
      const result = openWeeklyLearningGuideA4PreviewInNewTab(
        {
          v: 1,
          exportData: weeklyGuideExportPayload,
          fileNameBase: buildWeeklyGuideExportFileBase(),
        },
        action,
        { preOpenedWindow: placeholder }
      );
      if (!result.ok) {
        alert(openWeeklyLearningGuideA4PreviewFailureMessage(result.reason));
      }
    },
    [buildWeeklyGuideExportFileBase, weeklyGuideExportPayload]
  );

  const handleWeeklyGuideSavePdf = React.useCallback(() => {
    if (!canExportWeeklyGuideDocument || !weeklyGuideExportPayload) return;
    openWeeklyGuideA4PreviewTab('pdf');
  }, [canExportWeeklyGuideDocument, openWeeklyGuideA4PreviewTab, weeklyGuideExportPayload]);

  const handleWeeklyGuidePrint = React.useCallback(() => {
    if (!canExportWeeklyGuideDocument || !weeklyGuideExportPayload) return;
    openWeeklyGuideA4PreviewTab('print');
  }, [canExportWeeklyGuideDocument, openWeeklyGuideA4PreviewTab, weeklyGuideExportPayload]);

  const clearNeisAutoExportParams = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('neisAutoExport');
        next.delete('term');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  React.useEffect(() => {
    if (!neisAutoExportFormat) {
      neisAutoExportDoneRef.current = '';
      return;
    }
    if (loading || notesLoading) return;
    const runKey = `${workspaceId}|${academicYear}|${effectiveGradeId}|${termSelection}|${neisAutoExportFormat}`;
    if (neisAutoExportDoneRef.current === runKey) return;

    if (!neisClassTimetableMode) {
      setNeisClassTimetableMode(true);
      return;
    }
    if (termFromUrl != null && termSelection !== termFromUrl) {
      setTermSelection(termFromUrl);
      return;
    }
    if (!canExportNeisTermWeeklyGuide || !weeklyGuideExportPayload) {
      if (weeksByTerm[termSelection].length === 0 || periodSlots.length === 0) {
        neisAutoExportDoneRef.current = runKey;
        alert('NEIS 반별시간표로 내보낼 주간 데이터가 없습니다.');
        clearNeisAutoExportParams();
      }
      return;
    }

    neisAutoExportDoneRef.current = runKey;
    void (async () => {
      try {
        if (neisAutoExportFormat === 'xls') {
          setWeeklyGuideExportBusy(true);
          try {
            await saveWeeklyLearningGuideAsExcel(
              weeklyGuideExportPayload,
              `${buildWeeklyGuideExportFileBase()}.xlsx`
            );
          } finally {
            setWeeklyGuideExportBusy(false);
          }
        } else {
          openWeeklyGuideA4PreviewTab('pdf');
        }
      } catch (exportErr) {
        console.error('NEIS 반별시간표 바로 다운로드 실패:', exportErr);
        alert('다운로드에 실패했습니다.');
      } finally {
        clearNeisAutoExportParams();
      }
    })();
  }, [
    academicYear,
    buildWeeklyGuideExportFileBase,
    canExportNeisTermWeeklyGuide,
    clearNeisAutoExportParams,
    effectiveGradeId,
    notesLoading,
    loading,
    neisAutoExportFormat,
    neisClassTimetableMode,
    openWeeklyGuideA4PreviewTab,
    periodSlots.length,
    termFromUrl,
    termSelection,
    weeksByTerm,
    weeklyGuideExportPayload,
    workspaceId,
  ]);

  return (
    <section
      className={
        'cp-weekly-learning-guide-page' +
        (embedded ? ' cp-weekly-learning-guide-page--embedded' : '') +
        (embeddedTodoMode ? ' is-todo-mode' : '')
      }
    >
      <header className="cp-program-page-header cp-weekly-learning-guide-header">
        <div className="cp-weekly-learning-guide-header-main">
          <div className="cp-weekly-learning-guide-title-row">
            <div className="cp-weekly-learning-guide-title-neis-group">
              <h2 className="cp-weekly-learning-guide-main-title">
                {effectiveGradeId && weeklyGuideContainerTitleParts ? (
                  <>
                    <span className="cp-weekly-learning-guide-export-title-main">
                      {weeklyGuideContainerTitleParts.main}
                    </span>{' '}
                    <span className="cp-weekly-learning-guide-export-title-dates">
                      {weeklyGuideContainerTitleParts.dates}
                    </span>
                  </>
                ) : (
                  (effectiveGradeId && weeklyGuideContainerTitle) || '주간학습 안내'
                )}
              </h2>
              <div className="cp-annual-top-saturday-toggle cp-weekly-learning-guide-neis-toggle-wrap" aria-label="NEIS 반별시간표 입력 모드">
                <span className="cp-weekly-schedule-saturday-toggle-label">NEIS 반별시간표</span>
                <button
                  type="button"
                  className={'team-auto-approve-toggle' + (neisClassTimetableMode ? ' on' : '')}
                  onClick={toggleNeisClassTimetableMode}
                  aria-pressed={neisClassTimetableMode}
                  aria-label="NEIS 반별시간표 입력 모드"
                  disabled={!effectiveGradeId}
                  title="연간 시간표를 NEIS 반별시간표 입력 형식(교시당 한 칸·편제명)으로 봅니다."
                >
                  <span className="team-auto-approve-toggle-slider" data-on={!neisClassTimetableMode}>
                    OFF
                  </span>
                  <span className="team-auto-approve-toggle-slider" data-on={neisClassTimetableMode}>
                    ON
                  </span>
                </button>
              </div>
              {neisClassTimetableMode ? <NeisExtensionButton /> : null}
            </div>
            {effectiveGradeId ? (
              <div className="cp-weekly-learning-guide-header-toolbar-inline">
                <div className="cp-weekly-learning-guide-inline-term">
                  <select
                    value={termSelection}
                    aria-label="학기"
                    onChange={(e) => {
                      const tn = Number(e.target.value) as TermNumber;
                      setTermSelection(tn);
                      const list = weeksByTerm[tn];
                      const preferred = selectedWeekByTerm[tn];
                      const mon =
                        preferred && list.some((w) => w.mondayIso === preferred)
                          ? preferred
                          : list[0]?.mondayIso ?? '';
                      if (mon) {
                        setViewedTerm(tn);
                        setViewedMondayIso(mon);
                      }
                    }}
                    disabled={loading || availableTerms.length === 0}
                  >
                    {availableTerms.map((termNumber) => (
                      <option key={termNumber} value={termNumber}>
                        {termNumber}학기
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cp-weekly-learning-guide-week-row-inline">
                  <select
                    className="cp-weekly-learning-guide-week-select-input"
                    value={selectedWeekByTerm[termSelection]}
                    aria-label="주차 선택"
                    onChange={(e) => {
                      const mon = e.target.value;
                      setSelectedWeekByTerm((prev) => ({ ...prev, [termSelection]: mon }));
                      if (mon) {
                        setViewedTerm(termSelection);
                        setViewedMondayIso(mon);
                      }
                    }}
                    disabled={loading || weeksByTerm[termSelection].length === 0}
                  >
                    {weeksByTerm[termSelection].map((week) => (
                      <option key={week.mondayIso} value={week.mondayIso}>
                        {formatWeekOptionLabel(week)}
                      </option>
                    ))}
                  </select>
                  <div className="cp-weekly-learning-guide-week-nav" role="group" aria-label="주 이전·다음">
                    <button
                      type="button"
                      className="cp-weekly-learning-guide-week-nav-btn"
                      aria-label="이전 주"
                      disabled={!weekNavCanPrev}
                      onClick={() => navigateWeek('prev')}
                    >
                      <svg className="cp-weekly-learning-guide-week-nav-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14 6l-6 6 6 6"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="cp-weekly-learning-guide-week-nav-btn"
                      aria-label="다음 주"
                      disabled={!weekNavCanNext}
                      onClick={() => navigateWeek('next')}
                    >
                      <svg className="cp-weekly-learning-guide-week-nav-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M10 6l6 6-6 6"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="cp-weekly-learning-guide-header-actions">
                  <button
                    type="button"
                    className="cp-btn-secondary cp-weekly-learning-guide-home-notice-btn"
                    onClick={openManageModal}
                  >
                    가정통신 관리
                  </button>
                  <button
                    type="button"
                    className="cp-btn-secondary cp-weekly-learning-guide-track-color-btn"
                    onClick={openTrackColorModal}
                    disabled={loading}
                  >
                    시간표 색상 관리
                  </button>
                  <div className="cp-weekly-learning-guide-header-doc-actions">
                    <ModalDocumentActionButtons
                      onSaveExcel={() => void handleWeeklyGuideSaveExcel()}
                      onSavePdf={() => void handleWeeklyGuideSavePdf()}
                      onPrint={() => void handleWeeklyGuidePrint()}
                      saveDisabled={!canExportWeeklyGuideDocument}
                      saveExcelDisabled={!canExportWeeklyGuideDocument}
                      printDisabled={!canExportWeeklyGuideDocument}
                      busy={weeklyGuideExportBusy}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {neisClassTimetableMode ? <WeeklyLearningGuideNeisLegend /> : null}
        </div>
      </header>

      {!effectiveGradeId ? (
        <section className="cp-card cp-weekly-learning-guide-empty">
          <p className="page-desc">주간학습 안내를 보려면 먼저 클래스를 선택해 주세요.</p>
          <WeeklyLearningGuideEmptyFrame />
        </section>
      ) : (
        <>
          {error ? <p className="cp-weekly-learning-guide-error cp-weekly-learning-guide-page-error">{error}</p> : null}

          <section className="cp-card cp-weekly-learning-guide-table-card">
            {loading ? (
              <p className="page-desc">
                {embedded ? '시간표 데이터를 불러오는 중입니다.' : '주간학습 안내를 불러오는 중입니다.'}
              </p>
            ) : !viewedWeek || !viewedTerm || viewedColumns.length === 0 || periodSlots.length === 0 ? (
              <WeeklyLearningGuideEmptyFrame />
            ) : (
              <div ref={weeklyGuideExportRootRef} className="cp-weekly-learning-guide-export-root">
                <div className="cp-weekly-learning-guide-table-wrap">
                <table
                  ref={embeddedTableRef}
                  className="cp-weekly-learning-guide-table"
                  data-wlg-today={viewedColumns.find((column) => column.dateIso === headerTodayIso)?.weekdayKey}
                  style={
                    embedded && embeddedFit
                      ? ({
                          '--wlg-emb-block-fit': `${embeddedFit.block}px`,
                          '--wlg-emb-todo-h': `${embeddedFit.todo}px`,
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  <thead>
                    <tr>
                      <th rowSpan={2} className="cp-weekly-learning-guide-title-col">
                        구분
                      </th>
                      {viewedColumns.map((column) => (
                        <th
                          key={`head-${column.weekdayKey}`}
                          className={
                            'cp-weekly-learning-guide-day-head' +
                            (column.dateIso === headerTodayIso ? ' is-today' : '') +
                            (column.weekdayKey === 'sat' ? ' is-sat' : '')
                          }
                        >
                          {column.weekdayLabel}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {viewedColumns.map((column) => (
                        <th
                          key={`date-${column.weekdayKey}`}
                          className={
                            'cp-weekly-learning-guide-date-head' +
                            (column.dateIso === headerTodayIso ? ' is-today' : '') +
                            (column.weekdayKey === 'sat' ? ' is-sat' : '')
                          }
                        >
                          {column.inTerm ? (
                            <span className="cp-wlg-date-head-text">{formatDateHeader(column.dateIso)}</span>
                          ) : (
                            ''
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  {embeddedTodoMode ? (
                    <>
                    {renderWeeklyGuideBlockGap(viewedColumns.length)}
                    <tbody className="cp-wlg-todo-tbody">
                      <tr>
                        <th className="cp-weekly-learning-guide-period-title cp-wlg-todo-title">
                          TO
                          <br />
                          DO
                        </th>
                        {viewedColumns.map((column) => (
                          <td
                            key={`todo-${column.weekdayKey}`}
                            className={
                              'cp-wlg-todo-cell' +
                              (column.dateIso === headerTodayIso ? ' is-today' : '') +
                              (column.weekdayKey === 'sat' ? ' is-sat' : '')
                            }
                            data-wlg-day={column.weekdayKey}
                          >
                            <div className="cp-wlg-todo-cell-inner">
                              {embedded?.renderDayTodos?.(column.dateIso)}
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                    </>
                  ) : (
                  <>
                  {displayPeriodSlots.map((period, periodIdx) => (
                    <React.Fragment key={period.slotIndex}>
                    {renderWeeklyGuideBlockGap(viewedColumns.length)}
                    <tbody
                      className={[
                        'cp-weekly-learning-guide-period-tbody',
                        neisClassTimetableMode ? 'cp-weekly-learning-guide-period-tbody--neis' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {neisClassTimetableMode ? (
                        <tr className="cp-weekly-learning-guide-tr cp-weekly-learning-guide-tr--neis">
                          <th rowSpan={1} className="cp-weekly-learning-guide-period-title">
                            <PeriodTitleContent period={period} />
                          </th>
                          {viewedColumns.map((column, colIdx) => {
                            const display = getCellDisplay(
                              viewedTerm,
                              viewedWeek.mondayIso,
                              column.weekdayKey,
                              column.dateIso,
                              period.slotIndex
                            );
                            const surfaceCls = weeklyGuideSurfaceClassNames(display.slotSurface);
                            const neisBgClass =
                              display.slotSurface === 'class'
                                ? display.neisVsBase === 'match-base'
                                  ? 'cp-weekly-learning-guide-cell--neis-match-base'
                                  : display.neisVsBase === 'diff-track'
                                    ? 'cp-weekly-learning-guide-cell--neis-diff-track'
                                    : display.neisVsBase === 'dedicated-mismatch'
                                      ? 'cp-weekly-learning-guide-cell--neis-dedicated-mismatch'
                                      : ''
                                : '';
                            const mon = viewedWeek.mondayIso;
                            const term = viewedTerm;
                            const topNeighbor =
                              periodIdx > 0 &&
                              getCellDisplay(
                                term,
                                mon,
                                column.weekdayKey,
                                column.dateIso,
                                displayPeriodSlots[periodIdx - 1]!.slotIndex
                              ).neisShortIncreaseHighlight;
                            const bottomNeighbor =
                              periodIdx < displayPeriodSlots.length - 1 &&
                              getCellDisplay(
                                term,
                                mon,
                                column.weekdayKey,
                                column.dateIso,
                                displayPeriodSlots[periodIdx + 1]!.slotIndex
                              ).neisShortIncreaseHighlight;
                            const leftNeighbor =
                              colIdx > 0 &&
                              getCellDisplay(
                                term,
                                mon,
                                viewedColumns[colIdx - 1]!.weekdayKey,
                                viewedColumns[colIdx - 1]!.dateIso,
                                period.slotIndex
                              ).neisShortIncreaseHighlight;
                            const rightNeighbor =
                              colIdx < viewedColumns.length - 1 &&
                              getCellDisplay(
                                term,
                                mon,
                                viewedColumns[colIdx + 1]!.weekdayKey,
                                viewedColumns[colIdx + 1]!.dateIso,
                                period.slotIndex
                              ).neisShortIncreaseHighlight;
                            const shortEdgeStyle = buildNeisShortIncreaseEdgeStyle(
                              {
                                self: display.neisShortIncreaseHighlight,
                                topNeighbor,
                                bottomNeighbor,
                                leftNeighbor,
                                rightNeighbor,
                              },
                              resolveNeisShortEdgeColor()
                            );
                            return (
                              <td
                                key={`${period.slotIndex}-${column.weekdayKey}-neis`}
                                {...periodCellSelectableProps(
                                  column,
                                  period,
                                  [
                                    'cp-weekly-learning-guide-cell',
                                    'cp-weekly-learning-guide-cell--center',
                                    'cp-weekly-learning-guide-cell--neis-track',
                                    surfaceCls,
                                    neisBgClass,
                                    neisClassTimetableMode && display.neisShortIncreaseHighlight
                                      ? 'cp-weekly-learning-guide-cell--neis-short-increase'
                                      : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')
                                )}
                                style={shortEdgeStyle}
                              >
                                <WeeklyLearningGuideNeisCellContent
                                  slotSurface={display.slotSurface}
                                  subject={display.subject}
                                  neisTrackName={display.neisTrackName}
                                  neisUnregisteredTimetableText={display.neisUnregisteredTimetableText}
                                  neisDedicated={display.neisDedicated}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ) : (
                        <>
                          <tr className="cp-weekly-learning-guide-tr cp-weekly-learning-guide-tr--1line">
                            <th rowSpan={5} className="cp-weekly-learning-guide-period-title">
                              <PeriodTitleContent period={period} />
                            </th>
                            {viewedColumns.map((column) => {
                              const display = getCellDisplay(
                                viewedTerm,
                                viewedWeek.mondayIso,
                                column.weekdayKey,
                                column.dateIso,
                                period.slotIndex
                              );
                              const surfaceCls = weeklyGuideSurfaceClassNames(display.slotSurface);
                              return (
                                <td
                                  key={`${period.slotIndex}-${column.weekdayKey}-subject`}
                                  {...periodCellSelectableProps(
                                    column,
                                    period,
                                    [
                                      'cp-weekly-learning-guide-cell',
                                      'cp-weekly-learning-guide-cell--center',
                                      'cp-weekly-learning-guide-cell--subject-line',
                                      surfaceCls,
                                      display.slotSurface === 'class' && display.subjectTrackBgHex
                                        ? 'cp-weekly-learning-guide-cell--subject-track'
                                        : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' ')
                                  )}
                                  style={
                                    display.slotSurface === 'class' && display.subjectTrackBgHex
                                      ? ({
                                          backgroundColor: display.subjectTrackBgHex,
                                          '--wlg-track-color': display.subjectTrackBgHex,
                                        } as React.CSSProperties)
                                      : undefined
                                  }
                                >
                                  {display.slotSurface === 'class' && display.subjectTitle ? (
                                    <>
                                      <span className="cp-wlg-subject-doc">{display.subject}</span>
                                      <span className="cp-wlg-subject-card" hidden>
                                        <span className="cp-wlg-subject-card-row">
                                          <span className="cp-wlg-subject-card-names">
                                            <span className="cp-wlg-subject-card-title">{display.subjectTitle}</span>
                                            {display.subjectSubtitle ? (
                                              <span className="cp-wlg-subject-card-timetable">
                                                {display.subjectSubtitle}
                                              </span>
                                            ) : null}
                                          </span>
                                          {display.page ? (
                                            <span className="cp-wlg-subject-card-page">{display.page}</span>
                                          ) : null}
                                        </span>
                                      </span>
                                    </>
                                  ) : (
                                    display.subject
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                          <tr className="cp-weekly-learning-guide-tr cp-weekly-learning-guide-tr--1line">
                            {viewedColumns.map((column) => {
                              const display = getCellDisplay(
                                viewedTerm,
                                viewedWeek.mondayIso,
                                column.weekdayKey,
                                column.dateIso,
                                period.slotIndex
                              );
                              const surfaceCls = weeklyGuideSurfaceClassNames(display.slotSurface);
                              return (
                                <td
                                  key={`${period.slotIndex}-${column.weekdayKey}-unit`}
                                  {...periodCellSelectableProps(
                                    column,
                                    period,
                                    [
                                      'cp-weekly-learning-guide-cell',
                                      'cp-weekly-learning-guide-cell--center',
                                      display.slotSurface === 'class' ? 'cp-weekly-learning-guide-cell--unit' : '',
                                      surfaceCls,
                                    ]
                                      .filter(Boolean)
                                      .join(' ')
                                  )}
                                >
                                  {(() => {
                                    const menuSlot =
                                      display.slotSurface === 'class'
                                        ? slotsByWeekday[column.weekdayKey]?.find(
                                            (row) => row.slotIndex === period.slotIndex
                                          )
                                        : undefined;
                                    const evalCode = parseDashboardEvalStatusCode(menuSlot?.display.progressEvalCode);
                                    if (!evalCode) return display.unit;
                                    const evalLabel =
                                      (menuSlot?.display.progressEval ?? '').trim() || WEEKLY_GUIDE_EVAL_LABELS[evalCode];
                                    return (
                                      <span className="cp-wlg-unit-row">
                                        <span className="cp-wlg-unit-text">{display.unit}</span>
                                        <span
                                          className={`cp-wlg-eval-chip ${WEEKLY_GUIDE_EVAL_TONES[evalCode]}`}
                                          title={evalLabel}
                                        >
                                          <span className="cp-wlg-eval-chip-full">{evalLabel}</span>
                                          <span className="cp-wlg-eval-chip-short">평가</span>
                                        </span>
                                      </span>
                                    );
                                  })()}
                                </td>
                              );
                            })}
                          </tr>
                          <tr className="cp-weekly-learning-guide-tr cp-weekly-learning-guide-tr--learning">
                            {viewedColumns.map((column) => {
                              const display = getCellDisplay(
                                viewedTerm,
                                viewedWeek.mondayIso,
                                column.weekdayKey,
                                column.dateIso,
                                period.slotIndex
                              );
                              const surfaceCls = weeklyGuideSurfaceClassNames(display.slotSurface);
                              return (
                                <td
                                  key={`${period.slotIndex}-${column.weekdayKey}-learning`}
                                  {...periodCellSelectableProps(
                                    column,
                                    period,
                                    [
                                      'cp-weekly-learning-guide-cell',
                                      'cp-weekly-learning-guide-cell--top',
                                      display.slotSurface === 'class' ? 'cp-weekly-learning-guide-cell--class-slot' : '',
                                      surfaceCls,
                                    ]
                                      .filter(Boolean)
                                      .join(' ')
                                  )}
                                  style={weeklySubjectTrackTintStyle(display, 'learning')}
                                >
                                  <span className="cp-weekly-learning-guide-cell-learning-inner">{display.learning}</span>
                                </td>
                              );
                            })}
                          </tr>
                          <tr className="cp-weekly-learning-guide-tr cp-weekly-learning-guide-tr--1line cp-weekly-learning-guide-tr--page">
                            {viewedColumns.map((column) => {
                              const display = getCellDisplay(
                                viewedTerm,
                                viewedWeek.mondayIso,
                                column.weekdayKey,
                                column.dateIso,
                                period.slotIndex
                              );
                              const surfaceCls = weeklyGuideSurfaceClassNames(display.slotSurface);
                              return (
                                <td
                                  key={`${period.slotIndex}-${column.weekdayKey}-page`}
                                  {...periodCellSelectableProps(
                                    column,
                                    period,
                                    [
                                      'cp-weekly-learning-guide-cell',
                                      'cp-weekly-learning-guide-cell--center',
                                      surfaceCls,
                                    ]
                                      .filter(Boolean)
                                      .join(' ')
                                  )}
                                  style={weeklySubjectTrackTintStyle(display, 'page')}
                                >
                                  {display.page}
                                </td>
                              );
                            })}
                          </tr>
                          <tr className="cp-weekly-learning-guide-tr cp-weekly-learning-guide-tr--1line cp-weekly-learning-guide-tr--material">
                            {viewedColumns.map((column) => {
                              const display = getCellDisplay(
                                viewedTerm,
                                viewedWeek.mondayIso,
                                column.weekdayKey,
                                column.dateIso,
                                period.slotIndex
                              );
                              const surfaceCls = weeklyGuideSurfaceClassNames(display.slotSurface);
                              return (
                                <td
                                  key={`${period.slotIndex}-${column.weekdayKey}-material`}
                                  {...periodCellSelectableProps(
                                    column,
                                    period,
                                    [
                                      'cp-weekly-learning-guide-cell',
                                      'cp-weekly-learning-guide-cell--center',
                                      display.slotSurface === 'class' ? 'cp-weekly-learning-guide-cell--class-slot' : '',
                                      surfaceCls,
                                    ]
                                      .filter(Boolean)
                                      .join(' ')
                                  )}
                                  style={weeklySubjectTrackTintStyle(display, 'material')}
                                >
                                  {display.material ? (
                                    <WeeklyGuideMaterialMarquee text={display.material} />
                                  ) : null}
                                </td>
                              );
                            })}
                          </tr>
                        </>
                      )}
                    </tbody>
                    </React.Fragment>
                  ))}
                  {embedded ? null : (
                  <>
                  {renderWeeklyGuideBlockGap(viewedColumns.length)}
                  <tbody className="cp-weekly-learning-guide-notice-tbody">
                    <tr>
                      <th className="cp-weekly-learning-guide-period-title cp-weekly-learning-guide-period-title--notice">
                        가정
                        <br />
                        통신문
                      </th>
                      <td
                        colSpan={viewedColumns.length}
                        className="cp-weekly-learning-guide-notice-cell"
                        onClick={() => noticeTextareaRef.current?.focus()}
                      >
                        <div className="cp-weekly-learning-guide-notice-cell-inner">
                          <div className="cp-weekly-learning-guide-notice-export-text" aria-hidden="true">
                            {inlineNoticeText}
                          </div>
                          <textarea
                            ref={noticeTextareaRef}
                            className="cp-weekly-learning-guide-notice-textarea"
                            rows={5}
                            value={inlineNoticeText}
                            onChange={(e) => setInlineNoticeText(e.target.value)}
                            onBlur={() => {
                              void persistInlineNoticeIfChanged();
                            }}
                            disabled={loading || notesLoading || inlineNoticeSaving || !effectiveGradeId}
                            placeholder={notesLoading ? '가정통신을 불러오는 중입니다.' : undefined}
                            aria-label="가정통신문"
                          />
                          {inlineNoticeError ? (
                            <p className="cp-weekly-learning-guide-notice-inline-error" role="alert">
                              {inlineNoticeError}
                            </p>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  </>
                  )}
                  </>
                  )}
                </table>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {modalOpen ? (
        <div className="cp-weekly-learning-guide-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div
            className="cp-modal cp-weekly-learning-guide-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-weekly-learning-guide-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="cp-weekly-learning-guide-modal-header">
              <h3 id="cp-weekly-learning-guide-modal-title" className="cp-weekly-learning-guide-modal-title">
                가정통신 관리
              </h3>
              <div className="cp-weekly-learning-guide-modal-header-actions">
                <button type="button" className="cp-btn-save-all" onClick={saveManagedNotes} disabled={saving}>
                  {saving ? '저장 중...' : '저장하기'}
                </button>
                <button
                  type="button"
                  className="cp-weekly-learning-guide-modal-close"
                  onClick={() => setModalOpen(false)}
                  aria-label="닫기"
                >
                  <svg
                    className="cp-weekly-learning-guide-modal-close-icon"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    aria-hidden="true"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      d="M6 6l12 12M18 6L6 18"
                    />
                  </svg>
                </button>
              </div>
            </header>
            {saveError ? (
              <div className="cp-weekly-learning-guide-modal-error-strip" role="alert">
                <p className="cp-weekly-learning-guide-error">{saveError}</p>
              </div>
            ) : null}
            <div className="cp-weekly-learning-guide-modal-body-scroll">
              {([1, 2] as TermNumber[]).map((termNumber) =>
                weeksByTerm[termNumber].length === 0 ? null : (
                  <section key={termNumber} className="cp-weekly-learning-guide-note-section">
                    <h4>{termNumber}학기</h4>
                    {weeksByTerm[termNumber].map((week) => {
                      const draftKey = `${termNumber}|${week.mondayIso}`;
                      return (
                        <label key={draftKey} className="cp-weekly-learning-guide-note-block">
                          <span className="cp-weekly-learning-guide-note-label">{formatWeekOptionLabel(week)}</span>
                          <textarea
                            value={draftNotes[draftKey] ?? ''}
                            onChange={(e) =>
                              setDraftNotes((prev) => ({
                                ...prev,
                                [draftKey]: e.target.value,
                              }))
                            }
                            rows={5}
                          />
                        </label>
                      );
                    })}
                  </section>
                )
              )}
            </div>
          </div>
        </div>
      ) : null}

      {trackColorModalOpen ? (
        <div
          className="cp-weekly-learning-guide-modal-backdrop cp-weekly-learning-guide-modal-backdrop--track-colors"
          onClick={() => {
            if (!trackColorModalSaving && !trackColorResetConfirmOpen) closeTrackColorModal();
          }}
        >
          <div
            className="cp-modal cp-weekly-learning-guide-modal cp-weekly-learning-guide-track-color-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-weekly-learning-guide-track-color-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="cp-weekly-learning-guide-modal-header">
              <h3 id="cp-weekly-learning-guide-track-color-modal-title" className="cp-weekly-learning-guide-modal-title">
                시간표 색상 관리
              </h3>
              <div className="cp-weekly-learning-guide-modal-header-actions">
                <div className="cp-wlg-color-mode-toggle" role="radiogroup" aria-label="시간표 색상 모드">
                  {(
                    [
                      ['cell', '시간표셀색'],
                      ['track', '편제별 기본색'],
                    ] as const
                  ).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={draftColorMode === mode}
                      className={'cp-wlg-color-mode-toggle-btn' + (draftColorMode === mode ? ' is-active' : '')}
                      onClick={() => setDraftColorMode(mode)}
                      disabled={trackColorModalSaving}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="cp-btn-secondary"
                  disabled={
                    trackColorModalSaving || orderedTrackNamesForGrade.length === 0 || draftColorMode === 'cell'
                  }
                  onClick={() => setTrackColorResetConfirmOpen(true)}
                >
                  기본값으로 바꾸기
                </button>
                <button
                  type="button"
                  className="cp-weekly-learning-guide-modal-close"
                  onClick={closeTrackColorModal}
                  disabled={trackColorModalSaving}
                  aria-label="닫기"
                >
                  <svg
                    className="cp-weekly-learning-guide-modal-close-icon"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    aria-hidden="true"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      d="M6 6l12 12M18 6L6 18"
                    />
                  </svg>
                </button>
              </div>
            </header>
            {trackColorModalError ? (
              <div className="cp-weekly-learning-guide-modal-error-strip" role="alert">
                <p className="cp-weekly-learning-guide-error">{trackColorModalError}</p>
              </div>
            ) : null}
            <div className="cp-weekly-learning-guide-track-color-modal-body">
              {draftColorMode === 'cell' ? (
                <p className="page-desc cp-wlg-color-mode-hint">
                  연간시간표 셀에 지정된 색상을 그대로 사용합니다.
                </p>
              ) : orderedTrackNamesForGrade.length === 0 ? (
                <p className="page-desc">등록된 편제가 없습니다. 편제 설정 후 다시 시도해 주세요.</p>
              ) : (
                <ul className="cp-weekly-learning-guide-track-color-list">
                  {orderedTrackNamesForGrade.map((trackName) => {
                    const key = trackName.trim();
                    const hex = draftTrackColorMap[key] ?? '#CCCCCC';
                    const pickerValue = weeklyGuideColorPickerValue(hex);
                    return (
                      <li key={key} className="cp-weekly-learning-guide-track-color-item">
                        <span className="cp-weekly-learning-guide-track-color-item-label">{trackName}</span>
                        <div className="cp-weekly-learning-guide-track-color-swatch-wrap">
                          <input
                            type="color"
                            className="cp-weekly-learning-guide-track-color-input-overlay"
                            value={pickerValue}
                            onChange={(e) =>
                              setDraftTrackColorMap((prev) => ({
                                ...prev,
                                [key]: e.target.value.toUpperCase(),
                              }))
                            }
                            disabled={trackColorModalSaving}
                            aria-label={`${trackName} 색상 선택`}
                          />
                          <span
                            className="cp-weekly-learning-guide-track-color-swatch-visual"
                            style={{ backgroundColor: hex }}
                            aria-hidden
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="cp-modal-footer cp-leave-modal-actions cp-weekly-learning-guide-track-color-modal-actions">
              <button type="button" className="cp-btn-secondary" onClick={closeTrackColorModal} disabled={trackColorModalSaving}>
                닫기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void saveTrackColorsFromModal()}
                disabled={
                  trackColorModalSaving || (orderedTrackNamesForGrade.length === 0 && draftColorMode === 'track')
                }
              >
                {trackColorModalSaving ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {trackColorResetConfirmOpen ? (
        <div
          className="cp-weekly-learning-guide-modal-backdrop cp-weekly-learning-guide-modal-backdrop--confirm"
          onClick={() => !trackColorModalSaving && setTrackColorResetConfirmOpen(false)}
        >
          <div
            className="cp-modal cp-leave-modal-box cp-weekly-learning-guide-reset-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-weekly-color-reset-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-weekly-color-reset-title" className="cp-leave-modal-title">
              기본 색상으로 바꿀까요?
            </h3>
            <p className="cp-leave-modal-desc">
              편제별 색이 등록 순에 맞는 기본 팔레트로 초기화되고, 서버에 바로 저장됩니다.
            </p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setTrackColorResetConfirmOpen(false)}
                disabled={trackColorModalSaving}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void persistDefaultTrackColors()}
                disabled={trackColorModalSaving || orderedTrackNamesForGrade.length === 0}
              >
                {trackColorModalSaving ? '처리 중…' : '확인'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {lessonMenu ? (
        <DashboardWeeklyLessonActionMenu
          slot={lessonMenu.slot}
          anchorRect={lessonMenu.rect}
          titleVariant="weekly-guide"
          onClose={() => setLessonMenu(null)}
          onOpenPresentation={openWeeklyGuideSlotPresentation}
          onOpenProgram={openWeeklyGuideSlotProgram}
          onOpenLessonRecord={lessonSlotRecords.openLessonRecord}
          onOpenBehaviorRecord={lessonSlotRecords.openBehaviorRecord}
        />
      ) : null}
      <DashboardLessonSlotRecordModals records={lessonSlotRecords} idPrefix="cp-weekly-guide" />
    </section>
  );
};

export default WeeklyLearningGuidePage;
